import { appConfirm } from '../../services/appDialogs';
import { AppSelect } from '../ui/AppSelect';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AlertCircle,
  ArrowDown,
  ArrowRight,
  ArrowUp,
  Check,
  CheckCircle2,
  Clapperboard,
  Film,
  Image as ImageIcon,
  Loader2,
  MapPin,
  PackagePlus,
  Play,
  Plus,
  RefreshCw,
  Save,
  ScanSearch,
  Scissors,
  Sparkles,
  Trash2,
  Upload,
  Users,
  Video,
} from "lucide-react";
import { useToast } from "../../context/ToastContext";
import {
  analyzeVideoRepaint,
  createVideoRepaintKeyframeAsset,
  generateAllVideoRepaintShots,
  generateVideoRepaintShot,
  getVideoRepaintKeyframeAssetStatus,
  getVideoRepaintJob,
  listVideoRepaintJobs,
  reanalyzeVideoRepaint,
  updateVideoRepaintMappings,
  updateVideoRepaintReview,
  updateVideoRepaintShot,
} from "../../services/videoRepaintService";

const STATUS_META = {
  analyzing: { label: "分析中", color: "var(--color-accent-primary)" },
  ready: { label: "待确认", color: "var(--color-warning)" },
  pending: { label: "待生成", color: "var(--color-text-muted)" },
  processing: { label: "生成中", color: "var(--color-accent-primary)" },
  success: { label: "已完成", color: "var(--color-success)" },
  failed: { label: "失败", color: "var(--color-error)" },
};

const ANALYSIS_MODES = [
  { value: "hybrid", label: "双路线", description: "同时准备动作代理视频和视频提示词，逐镜选择生成方式" },
  { value: "direct", label: "动作代理视频", description: "用 FFmpeg 去除人脸细节，保留原片动作、节奏与运镜" },
  { value: "keyframe", label: "视频转提示词", description: "分析原片画面与动作，使用关键帧和提示词重绘" },
];

const GENERATION_MODES = [
  { value: "motion_proxy", label: "动作代理视频" },
  { value: "keyframe", label: "视频转提示词" },
];

const normalizeId = (value) => (value === null || value === undefined || value === "" ? "" : String(value));
const numericOrNull = (value) => (value === "" || value === null || value === undefined ? null : Number(value));
const finiteNumberOrNull = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};
const firstText = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number" && Number.isFinite(value)) return String(value);
  }
  return "";
};
const rawText = (value) => (value === null || value === undefined ? "" : String(value));
const statusKey = (value) => firstText(value).toLowerCase().replace(/[\s_-]+/g, "");
const isMotionProxyPreparing = (value) => ["notcreated", "preparing"].includes(statusKey(value));
const normalizeGenerationStrategy = (value) => (
  firstText(value).toLowerCase() === "keyframe" ? "keyframe" : "motion_proxy"
);

const ASSET_ACTIVE_STATUSES = new Set(["active"]);
const ASSET_PROCESSING_STATUSES = new Set(["processing", "pending", "queued", "creating", "submitted"]);
const ASSET_FAILED_STATUSES = new Set(["failed", "error", "rejected", "expired", "cancelled", "canceled"]);

const isAssetActive = (asset) => (
  Boolean(firstText(asset?.dreaminaProxyAssetId))
  && ASSET_ACTIVE_STATUSES.has(statusKey(firstText(asset?.dreaminaProxyAssetStatus)))
);

const isAssetProcessing = (asset) => (
  Boolean(firstText(asset?.dreaminaProxyAssetId))
  && ASSET_PROCESSING_STATUSES.has(statusKey(firstText(asset?.dreaminaProxyAssetStatus)))
);

const normalizeKeyframeAsset = (payload, fallback = {}) => {
  const candidate = payload?.keyframeAsset || payload?.shot?.keyframeAsset || payload;
  if (!candidate || typeof candidate !== "object") return fallback;
  const hasErrorField = Object.prototype.hasOwnProperty.call(candidate, "errorMessage")
    || Object.prototype.hasOwnProperty.call(candidate, "error");
  const hasAssetFields = [
    candidate.url,
    candidate.imageUrl,
    candidate.assetId,
    candidate.engGuaVideoAssetId,
    candidate.dreaminaProxyAssetId,
    candidate.status,
    candidate.engGuaVideoAssetStatus,
    candidate.dreaminaProxyAssetStatus,
  ].some((value) => value !== null && value !== undefined && value !== "");
  if (!hasAssetFields) return fallback;
  return {
    ...fallback,
    url: firstText(candidate.url, candidate.imageUrl, fallback?.url),
    assetId: firstText(candidate.dreaminaProxyAssetId, candidate.assetId, candidate.engGuaVideoAssetId, fallback?.assetId),
    status: firstText(candidate.dreaminaProxyAssetStatus, candidate.status, candidate.engGuaVideoAssetStatus, fallback?.status),
    dreaminaProxyAssetId: firstText(candidate.dreaminaProxyAssetId, fallback?.dreaminaProxyAssetId),
    dreaminaProxyAssetStatus: firstText(candidate.dreaminaProxyAssetStatus, fallback?.dreaminaProxyAssetStatus),
    provider: firstText(candidate.provider, fallback?.provider, "engguavideo"),
    resourceId: candidate.resourceId ?? fallback?.resourceId ?? null,
    errorMessage: hasErrorField
      ? firstText(candidate.errorMessage, candidate.error)
      : "",
  };
};

const normalizeDialogueLines = (shot) => {
  const lines = Array.isArray(shot?.dialogueLines) ? shot.dialogueLines : [];
  if (lines.length > 0) {
    return lines.map((line, index) => ({
      lineId: firstText(line?.lineId),
      lineIndex: finiteNumberOrNull(line?.lineIndex) ?? index,
      speaker: firstText(line?.speakerCharacterKey, line?.speakerKey, line?.speaker),
      shotKey: firstText(line?.shotKey, shot?.shotKey),
      startSeconds: finiteNumberOrNull(line?.startSeconds),
      endSeconds: finiteNumberOrNull(line?.endSeconds),
      sourceText: firstText(line?.sourceText),
      englishText: firstText(line?.englishText),
      verificationStatus: firstText(line?.verificationStatus),
      requiresReview: Boolean(line?.requiresReview),
      evidenceSources: Array.isArray(line?.evidenceSources) ? [...line.evidenceSources] : [],
      reviewReason: rawText(line?.reviewReason),
    }));
  }
  const sourceText = firstText(shot?.sourceDialogue);
  const englishText = firstText(shot?.englishDialogue);
  if (!sourceText && !englishText) return [];
  return [{
    lineIndex: 0,
    speaker: "",
    startSeconds: finiteNumberOrNull(shot?.startSeconds),
    endSeconds: finiteNumberOrNull(shot?.endSeconds),
    sourceText,
    englishText,
  }];
};

const dialogueLinesToEnglishDialogue = (lines) => (lines || [])
  .map((line) => firstText(line?.englishText))
  .filter(Boolean)
  .join("\n");

const dialogueLinesRevision = (lines) => JSON.stringify((lines || []).map((line) => [
  firstText(line?.lineId),
  finiteNumberOrNull(line?.lineIndex),
  rawText(line?.sourceText),
  rawText(line?.englishText),
]));

const REVIEW_SPECIAL_SPEAKERS = [
  { value: "UNKNOWN", label: "待确认（UNKNOWN）" },
  { value: "NARRATOR", label: "旁白（NARRATOR）" },
  { value: "OFFSCREEN", label: "画外音（OFFSCREEN）" },
];
const RELATIONSHIP_TYPE_SUGGESTIONS = ["UNKNOWN", "FAMILY", "FRIEND", "ROMANTIC", "WORK", "RIVAL", "OTHER"];
let reviewDraftSequence = 0;
const nextReviewDraftId = (prefix) => `${prefix}_${Date.now()}_${++reviewDraftSequence}`;

const normalizeReviewDialogueLine = (line, index, fallbackShotKey = "") => ({
  lineId: firstText(line?.lineId) || `REVIEW_LINE_${index + 1}`,
  lineIndex: finiteNumberOrNull(line?.lineIndex) ?? index,
  speakerCharacterKey: firstText(line?.speakerCharacterKey, line?.speakerKey, line?.speaker),
  shotKey: firstText(line?.shotKey, fallbackShotKey),
  startSeconds: finiteNumberOrNull(line?.startSeconds),
  endSeconds: finiteNumberOrNull(line?.endSeconds),
  sourceText: rawText(line?.sourceText),
  englishText: rawText(line?.englishText),
  verificationStatus: firstText(line?.verificationStatus),
  requiresReview: Boolean(line?.requiresReview),
  evidenceSources: Array.isArray(line?.evidenceSources) ? [...line.evidenceSources] : [],
  reviewReason: rawText(line?.reviewReason),
});

const normalizeReviewRelationship = (relationship, index) => ({
  relationshipId: firstText(relationship?.relationshipId, relationship?.id),
  clientKey: firstText(relationship?.relationshipId, relationship?.id) || `relationship-${index}`,
  from: firstText(relationship?.from, relationship?.fromCharacterKey),
  to: firstText(relationship?.to, relationship?.toCharacterKey),
  type: firstText(relationship?.type, relationship?.relationshipType, "UNKNOWN"),
  evidence: rawText(relationship?.evidence),
  evidenceLineIds: Array.isArray(relationship?.evidenceLineIds)
    ? relationship.evidenceLineIds.map(rawText).filter(Boolean)
    : [],
  evidenceShotKeys: Array.isArray(relationship?.evidenceShotKeys)
    ? relationship.evidenceShotKeys.map(rawText).filter(Boolean)
    : [],
  confidence: relationship?.confidence ?? null,
  status: firstText(relationship?.status),
  requiresReview: Boolean(relationship?.requiresReview),
});

const createReviewDraft = (job) => {
  if (!job?.jobId) return null;
  const allowedSpeakerKeys = new Set([
    ...(job.characters || []).map((character) => character.sourceKey),
    ...REVIEW_SPECIAL_SPEAKERS.map((item) => item.value),
  ]);
  const wholeFilmDialogue = Array.isArray(job.dialogueLedger)
    ? job.dialogueLedger
    : Array.isArray(job.dialogueLines)
      ? job.dialogueLines
      : null;
  const dialogueSource = wholeFilmDialogue
    ? wholeFilmDialogue
    : (job.shots || []).flatMap((shot) => (
      normalizeDialogueLines(shot).map((line) => ({ ...line, shotKey: shot.shotKey }))
    ));
  return {
    jobId: job.jobId,
    dialogueLines: dialogueSource.map((line, index) => {
      const normalized = normalizeReviewDialogueLine(line, index);
      return {
        ...normalized,
        speakerCharacterKey: allowedSpeakerKeys.has(normalized.speakerCharacterKey)
          ? normalized.speakerCharacterKey
          : "UNKNOWN",
      };
    }),
    dialogueConfirmed: Boolean(job.dialogueConfirmed),
    relationships: (Array.isArray(job.relationships) ? job.relationships : [])
      .map(normalizeReviewRelationship),
    relationshipsConfirmed: Boolean(job.relationshipsConfirmed),
    reviewRequired: Boolean(job.reviewRequired),
  };
};

const reindexDialogueLines = (lines) => lines.map((line, index) => ({ ...line, lineIndex: index }));
const hasMultilineReviewDialogueText = (line) => (
  /[\r\n]/.test(rawText(line?.sourceText)) || /[\r\n]/.test(rawText(line?.englishText))
);
const hasUnresolvedReviewDialogueLine = (line) => (
  !rawText(line?.sourceText).trim()
  || !rawText(line?.englishText).trim()
  || !firstText(line?.shotKey)
  || !firstText(line?.speakerCharacterKey)
  || line?.speakerCharacterKey === "UNKNOWN"
  || hasMultilineReviewDialogueText(line)
);

const splitReviewText = (value) => {
  const source = rawText(value).trim();
  if (source.length < 2) return [source, ""];
  const midpoint = source.length / 2;
  const candidates = [];
  for (let index = 1; index < source.length - 1; index += 1) {
    if (/[\s，,。！？!?；;]/.test(source[index])) candidates.push(index + 1);
  }
  const splitAt = candidates.length > 0
    ? candidates.reduce((best, candidate) => (
      Math.abs(candidate - midpoint) < Math.abs(best - midpoint) ? candidate : best
    ), candidates[0])
    : Math.ceil(midpoint);
  return [source.slice(0, splitAt).trim(), source.slice(splitAt).trim()];
};

const joinReviewText = (first, second) => [rawText(first).trim(), rawText(second).trim()]
  .filter(Boolean)
  .join(" ");

const reviewVerificationMeta = (status, requiresReview) => {
  const key = statusKey(status);
  if (requiresReview || ["requiresreview", "needsreview", "unverified"].includes(key)) {
    return { label: firstText(status, "需核对"), color: "var(--color-warning)" };
  }
  if (["verified", "confirmed", "approved"].includes(key)) {
    return { label: firstText(status, "已核对"), color: "var(--color-success)" };
  }
  return { label: firstText(status, "待核对"), color: "var(--color-text-muted)" };
};

const REVIEW_REASON_LABELS = {
  cross_chunk_transcription_variant: "相邻音频分段的转写不一致",
  audio_audiovisual_text_mismatch: "独立音频与音画识别的原文不一致",
  audio_audiovisual_speaker_conflict: "独立音频与音画识别的说话人不一致",
  missing_audiovisual_dialogue_evidence: "仅独立音频识别到这句",
  missing_independent_audio_evidence: "仅音画识别到这句",
  speaker_not_bound_to_character: "说话人尚未绑定到源人物",
  manual_line_not_found_in_automatic_transcript: "人工补充的台词未在自动转写中找到",
  detected_line_missing_from_manual_transcript: "自动转写台词未在人工稿中找到",
  manual_text_fuzzy_matched: "人工稿与自动转写仅为近似匹配",
  dialogue_segment_conflicts_with_timeline: "台词跨越镜头边界，已按实际开始时间归入镜头",
};

const reviewEvidenceSummary = (line) => {
  const sources = (line?.evidenceSources || []).map((source) => (
    source === "audio" ? "独立音频" : source === "audiovisual" ? "音画识别" : source
  ));
  const reason = REVIEW_REASON_LABELS[line?.reviewReason] || rawText(line?.reviewReason);
  return [sources.length > 0 ? `证据：${sources.join(" + ")}` : "", reason].filter(Boolean).join("；");
};

const formatConfidence = (value) => {
  const confidence = Number(value);
  if (!Number.isFinite(confidence)) return "未提供";
  return confidence <= 1 ? `${Math.round(confidence * 100)}%` : `${Math.round(confidence)}%`;
};

const formatTimestamp = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds < 0) return "";
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds - minutes * 60;
  return `${String(minutes).padStart(2, "0")}:${remainder.toFixed(2).padStart(5, "0")}`;
};

const generationBlockReason = (shot, strategyOverride, dialogueLinesOverride) => {
  const dialogueLines = Array.isArray(dialogueLinesOverride)
    ? dialogueLinesOverride
    : normalizeDialogueLines(shot);
  const untranslatedCount = dialogueLines.filter((line) => (
    firstText(line?.sourceText) && !firstText(line?.englishText)
  )).length;
  if (untranslatedCount > 0) return `还有 ${untranslatedCount} 句原片台词没有英文翻译`;
  const strategy = normalizeGenerationStrategy(firstText(strategyOverride, shot?.generationStrategy));
  if (strategy === "motion_proxy") {
    if (statusKey(shot?.motionProxyStatus) !== "ready" || !firstText(shot?.motionProxyUrl)) {
      return firstText(shot?.motionProxyError, "当前镜头的动作代理视频尚未准备完成");
    }
    return "";
  }
  if (!firstText(shot?.referenceFrameUrls?.[0])) return "当前镜头没有可提交的关键帧";
  if (!isAssetActive(shot?.keyframeAsset)) return "关键帧尚未在 Dreamina v3 新代理库完成入库；旧 EngGuaVideo ID 不可复用，请从原图重新入库";
  return "";
};

const KEYFRAME_STATE_META = {
  unregistered: { label: "未入新库", color: "var(--color-warning)", description: "旧海外素材 ID 不算新库就绪；请用关键帧原图重新入 Dreamina v3 库。" },
  processing: { label: "处理中", color: "var(--color-accent-primary)", description: "Dreamina v3 正在处理关键帧素材，达到 Active 后才能生成。" },
  active: { label: "已入新库", color: "var(--color-success)", description: "关键帧已在 Dreamina v3 新代理库就绪。" },
  failed: { label: "入库失败", color: "var(--color-error)", description: "关键帧入库失败，可以直接重新入库。" },
};

const MOTION_PROXY_PREP_META = {
  notcreated: { label: "等待处理", color: "var(--color-text-muted)", description: "动作代理视频已进入后台准备队列。" },
  preparing: { label: "处理中", color: "var(--color-accent-primary)", description: "正在用 FFmpeg 生成去身份动作代理视频。" },
  ready: { label: "已就绪", color: "var(--color-success)", description: "临时代理视频保留原片动作、构图和运镜，将直接作为本镜头的动作参考。" },
  failed: { label: "处理失败", color: "var(--color-error)", description: "动作代理视频生成失败，请重新分析源视频。" },
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.pending;
  return (
    <span
      className="inline-flex h-6 shrink-0 items-center rounded-md px-2 text-[11px] font-semibold"
      style={{
        color: meta.color,
        backgroundColor: `color-mix(in srgb, ${meta.color} 11%, transparent)`,
        border: `1px solid color-mix(in srgb, ${meta.color} 28%, transparent)`,
      }}
    >
      {meta.label}
    </span>
  );
};

const EmptyImage = ({ icon: Icon = ImageIcon, label }) => (
  <div className="flex h-full min-h-24 w-full flex-col items-center justify-center gap-2" style={{ color: "var(--color-text-muted)" }}>
    <Icon size={22} strokeWidth={1.5} />
    <span className="text-xs">{label}</span>
  </div>
);

const VideoRepaintWorkbench = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const fileInputRef = useRef(null);
  const mappingJobIdRef = useRef(null);
  const [jobs, setJobs] = useState([]);
  const [job, setJob] = useState(null);
  const [selectedShotId, setSelectedShotId] = useState(null);
  const [showUploader, setShowUploader] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [reanalyzingJobId, setReanalyzingJobId] = useState(null);
  const [savingMappings, setSavingMappings] = useState(false);
  const [savingShot, setSavingShot] = useState(false);
  const [generatingAll, setGeneratingAll] = useState(false);
  const [generatingShotId, setGeneratingShotId] = useState(null);
  const [sourceFile, setSourceFile] = useState(null);
  const [analysisMode, setAnalysisMode] = useState("hybrid");
  const [transcript, setTranscript] = useState("");
  const [mappingTab, setMappingTab] = useState("characters");
  const [reviewTab, setReviewTab] = useState("dialogue");
  const [characterTargets, setCharacterTargets] = useState({});
  const [sceneTargets, setSceneTargets] = useState({});
  const [mappingDirty, setMappingDirty] = useState(false);
  const [reviewDraft, setReviewDraft] = useState(null);
  const [reviewDirty, setReviewDirty] = useState(false);
  const [savingReview, setSavingReview] = useState(false);
  const [shotDraft, setShotDraft] = useState(null);
  const [keyframeActionByScope, setKeyframeActionByScope] = useState({});
  const [keyframeErrorByScope, setKeyframeErrorByScope] = useState({});
  const keyframeCompletionNoticeRef = useRef(new Set());

  const loadJob = useCallback(async (jobId, { quiet = false } = {}) => {
    try {
      const data = await getVideoRepaintJob(jobId);
      setJob(data);
      setJobs((previous) => previous.map((item) => (item.jobId === data.jobId ? { ...item, ...data } : item)));
      return data;
    } catch (error) {
      if (!quiet) toast.error(error.message || "读取转绘任务失败", 0);
      return null;
    }
  }, [toast]);

  const loadJobs = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const data = await listVideoRepaintJobs(projectId);
      const items = Array.isArray(data) ? data : [];
      setJobs(items);
      if (items.length > 0) {
        await loadJob(items[0].jobId, { quiet: true });
        setShowUploader(false);
      } else {
        setShowUploader(true);
      }
    } catch (error) {
      toast.error(error.message || "读取转绘项目失败", 0);
      setShowUploader(true);
    } finally {
      setLoading(false);
    }
  }, [loadJob, projectId, toast]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  const hasProcessingShot = useMemo(
    () => (job?.shots || []).some((shot) => shot.status === "processing"),
    [job?.shots]
  );
  const hasPreparingMotionProxy = useMemo(
    () => (job?.shots || []).some((shot) => isMotionProxyPreparing(shot?.motionProxyStatus)),
    [job?.shots]
  );

  useEffect(() => {
    if (!job?.jobId || (job.status !== "analyzing" && !hasProcessingShot && !hasPreparingMotionProxy)) return undefined;
    const timer = window.setInterval(() => loadJob(job.jobId, { quiet: true }), 3000);
    return () => window.clearInterval(timer);
  }, [hasPreparingMotionProxy, hasProcessingShot, job?.jobId, job?.status, loadJob]);

  const mappingRevision = useMemo(() => JSON.stringify({
    characters: (job?.characters || []).map((item) => [item.sourceKey, normalizeId(item.targetCharacterId)]),
    scenes: (job?.scenes || []).map((item) => [item.sourceKey, normalizeId(item.targetSceneId)]),
  }), [job?.characters, job?.scenes]);

  useEffect(() => {
    if (!job?.jobId) return;
    const jobChanged = mappingJobIdRef.current !== job.jobId;
    if (!jobChanged && mappingDirty) return;
    const nextCharacters = {};
    (job.characters || []).forEach((item) => {
      nextCharacters[item.sourceKey] = normalizeId(item.targetCharacterId);
    });
    const nextScenes = {};
    (job.scenes || []).forEach((item) => {
      nextScenes[item.sourceKey] = normalizeId(item.targetSceneId);
    });
    setCharacterTargets(nextCharacters);
    setSceneTargets(nextScenes);
    mappingJobIdRef.current = job.jobId;
    setMappingDirty(false);
  }, [job?.jobId, mappingDirty, mappingRevision]);

  const shots = job?.shots || [];
  const selectedShot = useMemo(
    () => shots.find((item) => String(item.id) === String(selectedShotId)) || shots[0] || null,
    [selectedShotId, shots]
  );
  const selectedShotScope = job?.jobId && selectedShot?.id ? `${job.jobId}:${selectedShot.id}` : "";
  const selectedKeyframeAsset = useMemo(() => normalizeKeyframeAsset(selectedShot?.keyframeAsset, {
    url: firstText(selectedShot?.referenceFrameUrls?.[0]),
    provider: "engguavideo",
  }), [selectedShot]);
  const selectedKeyframeUrl = firstText(selectedKeyframeAsset.url, selectedShot?.referenceFrameUrls?.[0]);
  const selectedKeyframeAction = selectedShotScope ? keyframeActionByScope[selectedShotScope] || "" : "";
  const selectedKeyframeError = selectedShotScope ? keyframeErrorByScope[selectedShotScope] || "" : "";
  const selectedMotionProxyUrl = firstText(selectedShot?.motionProxyUrl);

  useEffect(() => {
    if (!selectedShot) {
      setSelectedShotId(null);
      setShotDraft(null);
      return;
    }
    setSelectedShotId(selectedShot.id);
    const dialogueLines = normalizeDialogueLines(selectedShot);
    setShotDraft({
      generationStrategy: normalizeGenerationStrategy(selectedShot.generationStrategy),
      prompt: selectedShot.prompt || "",
      dialogueLines,
      englishDialogue: dialogueLines.length > 0
        ? dialogueLinesToEnglishDialogue(dialogueLines)
        : selectedShot.englishDialogue || "",
      targetCharacterIds: (selectedShot.targetCharacterIds || []).map(normalizeId),
      targetSceneId: normalizeId(selectedShot.targetSceneId),
    });
  }, [job?.jobId, selectedShot?.id]);

  const requiredCharacterKeys = useMemo(
    () => new Set(shots.flatMap((shot) => shot.sourceCharacterKeys || [])),
    [shots]
  );
  const requiredSceneKeys = useMemo(
    () => new Set(shots.map((shot) => shot.sourceSceneKey).filter(Boolean)),
    [shots]
  );
  const sourceCharactersByKey = useMemo(
    () => new Map((job?.characters || []).map((item) => [item.sourceKey, item])),
    [job?.characters]
  );
  const sourceScenesByKey = useMemo(
    () => new Map((job?.scenes || []).map((item) => [item.sourceKey, item])),
    [job?.scenes]
  );
  const reviewSpeakerKeys = useMemo(() => new Set([
    ...(job?.characters || []).map((character) => character.sourceKey),
    ...REVIEW_SPECIAL_SPEAKERS.map((item) => item.value),
  ]), [job?.characters]);
  const reviewShotKeys = useMemo(
    () => new Set(shots.map((shot) => shot.shotKey)),
    [shots]
  );
  const targetCharactersById = useMemo(
    () => new Map((job?.workshopCharacters || []).map((item) => [normalizeId(item.id), item])),
    [job?.workshopCharacters]
  );
  const targetScenesById = useMemo(
    () => new Map((job?.workshopScenes || []).map((item) => [normalizeId(item.id), item])),
    [job?.workshopScenes]
  );
  const jobReviewDraft = useMemo(() => createReviewDraft(job), [
    job?.dialogueConfirmed,
    job?.dialogueLedger,
    job?.dialogueLines,
    job?.jobId,
    job?.relationships,
    job?.relationshipsConfirmed,
    job?.reviewRequired,
    job?.shots,
  ]);
  const reviewHasUnsavedChanges = Boolean(
    reviewDirty && reviewDraft?.jobId && reviewDraft.jobId === job?.jobId
  );
  const activeReviewDraft = reviewHasUnsavedChanges ? reviewDraft : jobReviewDraft;
  const hasMultipleSourceCharacters = (job?.characters || []).length > 1;
  const reviewBlockingReason = !activeReviewDraft
    ? "台词与人物关系审核数据尚未就绪"
    : reviewHasUnsavedChanges
      ? "台词与人物关系审核有未保存修改"
      : !activeReviewDraft.dialogueConfirmed
        ? "全片台词尚未确认"
        : hasMultipleSourceCharacters && !activeReviewDraft.relationshipsConfirmed
          ? "多人镜头的人物关系尚未确认"
          : "";
  const missingMappingCount = useMemo(() => {
    let count = 0;
    requiredCharacterKeys.forEach((key) => { if (!characterTargets[key]) count += 1; });
    requiredSceneKeys.forEach((key) => { if (!sceneTargets[key]) count += 1; });
    return count;
  }, [characterTargets, requiredCharacterKeys, requiredSceneKeys, sceneTargets]);

  const mappedTargetsForShot = useCallback((shot) => {
    const targetCharacterIds = (shot?.sourceCharacterKeys || [])
      .map((key) => normalizeId(characterTargets[key]))
      .filter(Boolean);
    return {
      targetCharacterIds,
      targetSceneId: normalizeId(sceneTargets[shot?.sourceSceneKey]),
    };
  }, [characterTargets, sceneTargets]);

  const selectedShotMissingMappingCount = useMemo(() => {
    if (!selectedShot) return 0;
    const targets = mappedTargetsForShot(selectedShot);
    const requiredCharacters = (selectedShot.sourceCharacterKeys || []).length;
    const missingCharacters = Math.max(0, requiredCharacters - targets.targetCharacterIds.length);
    const missingScene = selectedShot.sourceSceneKey && !targets.targetSceneId ? 1 : 0;
    return missingCharacters + missingScene;
  }, [mappedTargetsForShot, selectedShot]);

  const setKeyframeScopeAction = useCallback((scope, action) => {
    if (!scope) return;
    setKeyframeActionByScope((previous) => ({ ...previous, [scope]: action }));
  }, []);

  const setKeyframeScopeError = useCallback((scope, message) => {
    if (!scope) return;
    setKeyframeErrorByScope((previous) => ({ ...previous, [scope]: firstText(message) }));
  }, []);

  const patchShotKeyframeAsset = useCallback((targetJobId, targetShotId, payload) => {
    setJob((previous) => {
      if (!previous || String(previous.jobId) !== String(targetJobId)) return previous;
      return {
        ...previous,
        shots: (previous.shots || []).map((shot) => {
          if (String(shot.id) !== String(targetShotId)) return shot;
          return {
            ...shot,
            keyframeAsset: normalizeKeyframeAsset(payload, {
              ...(shot.keyframeAsset || {}),
              url: firstText(shot.keyframeAsset?.url, shot.referenceFrameUrls?.[0]),
              provider: "engguavideo",
            }),
          };
        }),
      };
    });
  }, []);

  const ingestKeyframeAsset = useCallback(async ({ targetJobId, targetShotId, scope, url, name }) => {
    if (!projectId || !targetJobId || !targetShotId || !scope || !url) return null;
    setKeyframeScopeAction(scope, "asset-create");
    setKeyframeScopeError(scope, "");
    try {
      const result = await createVideoRepaintKeyframeAsset({ projectId, url, name });
      const asset = normalizeKeyframeAsset(result, { provider: "engguavideo", url });
      patchShotKeyframeAsset(targetJobId, targetShotId, result);
      if (ASSET_FAILED_STATUSES.has(statusKey(asset.status))) {
        const message = firstText(asset.errorMessage, "关键帧入库失败，请重新尝试");
        setKeyframeScopeError(scope, message);
        toast.error(message, 0);
      } else if (isAssetActive(asset)) {
        toast.success("关键帧已完成 Dreamina v3 入库");
      } else {
        toast.success("关键帧已提交 Dreamina v3 入库，正在处理");
      }
      return asset;
    } catch (error) {
      const message = error.message || "关键帧入库失败";
      setKeyframeScopeError(scope, message);
      toast.error(message, 0);
      return null;
    } finally {
      setKeyframeActionByScope((previous) => (
        previous[scope] === "asset-create" ? { ...previous, [scope]: "" } : previous
      ));
    }
  }, [patchShotKeyframeAsset, projectId, setKeyframeScopeAction, setKeyframeScopeError, toast]);

  const refreshKeyframeAsset = useCallback(async ({ targetJobId, targetShotId, scope, url, assetId }) => {
    if (!projectId || !targetJobId || !targetShotId || !scope || !url || !assetId) return null;
    try {
      const result = await getVideoRepaintKeyframeAssetStatus({ projectId, url, assetId });
      const asset = normalizeKeyframeAsset(result, { provider: "engguavideo", url, assetId });
      patchShotKeyframeAsset(targetJobId, targetShotId, result);
      if (ASSET_FAILED_STATUSES.has(statusKey(asset.status))) {
        setKeyframeScopeError(scope, firstText(asset.errorMessage, "关键帧入库失败，请重新入库"));
      } else {
        setKeyframeScopeError(scope, "");
      }
      return asset;
    } catch (error) {
      const message = error.message || "刷新关键帧入库状态失败";
      setKeyframeScopeError(scope, message);
      return null;
    }
  }, [patchShotKeyframeAsset, projectId, setKeyframeScopeError]);

  const keyframeAssetPolling = isAssetProcessing(selectedKeyframeAsset);

  useEffect(() => {
    if (!keyframeAssetPolling || !job?.jobId || !selectedShot?.id || !selectedShotScope) return undefined;
    const targetJobId = job.jobId;
    const targetShotId = selectedShot.id;
    const scope = selectedShotScope;
    const url = selectedKeyframeUrl;
    const assetId = firstText(selectedKeyframeAsset.dreaminaProxyAssetId);
    let cancelled = false;
    let inFlight = false;

    const poll = async () => {
      if (cancelled || inFlight) return;
      inFlight = true;
      try {
        const asset = await refreshKeyframeAsset({ targetJobId, targetShotId, scope, url, assetId });
        if (cancelled || !asset) return;
        if (isAssetActive(asset)) {
          const noticeKey = `${scope}:${firstText(asset.assetId)}`;
          if (!keyframeCompletionNoticeRef.current.has(noticeKey)) {
            keyframeCompletionNoticeRef.current.add(noticeKey);
            toast.success("关键帧已完成 Dreamina v3 入库，现在可以生成镜头");
          }
        } else if (ASSET_FAILED_STATUSES.has(statusKey(asset.status))) {
          toast.error(firstText(asset.errorMessage, "关键帧入库失败，请重新入库"), 0);
        }
      } finally {
        inFlight = false;
      }
    };

    const timer = window.setInterval(poll, 3000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [
    job?.jobId,
    keyframeAssetPolling,
    refreshKeyframeAsset,
    selectedKeyframeAsset.dreaminaProxyAssetId,
    selectedKeyframeUrl,
    selectedShot?.id,
    selectedShotScope,
    toast,
  ]);

  const selectedKeyframeDisplayError = firstText(selectedKeyframeError, selectedKeyframeAsset.errorMessage);
  const selectedAssetFailed = ASSET_FAILED_STATUSES.has(statusKey(selectedKeyframeAsset.dreaminaProxyAssetStatus))
    || Boolean(selectedKeyframeDisplayError && !isAssetProcessing(selectedKeyframeAsset));
  const selectedKeyframeState = isAssetActive(selectedKeyframeAsset)
    ? "active"
    : selectedAssetFailed
      ? "failed"
      : isAssetProcessing(selectedKeyframeAsset)
        ? "processing"
        : "unregistered";
  const selectedKeyframeStateMeta = KEYFRAME_STATE_META[selectedKeyframeState];
  const selectedGenerationBlockReason = firstText(
    reviewBlockingReason,
    generationBlockReason(
      selectedShot,
      shotDraft?.generationStrategy,
      shotDraft?.dialogueLines
    )
  );
  const batchGenerationBlocks = useMemo(() => shots
    .filter((shot) => shot.status !== "processing" && shot.status !== "success")
    .map((shot) => ({
      shot,
      reason: firstText(
        reviewBlockingReason,
        generationBlockReason(
          shot,
          String(shot.id) === String(selectedShot?.id) ? shotDraft?.generationStrategy : shot.generationStrategy,
          String(shot.id) === String(selectedShot?.id) ? shotDraft?.dialogueLines : undefined
        )
      ),
    }))
    .filter((item) => item.reason), [reviewBlockingReason, selectedShot?.id, shotDraft?.dialogueLines, shotDraft?.generationStrategy, shots]);
  const selectedKeyframeBusy = Boolean(selectedKeyframeAction) || keyframeAssetPolling;
  const selectedKeyframePrimaryLabel = selectedKeyframeAction === "asset-create"
    ? "入库中"
    : keyframeAssetPolling
      ? "入库中"
      : isAssetActive(selectedKeyframeAsset)
        ? "已入库"
        : selectedAssetFailed
          ? "重新入库"
          : "关键帧入库";
  const selectedMotionProxyPrepStatus = statusKey(selectedShot?.motionProxyStatus) || "notcreated";
  const selectedMotionProxyPrepMeta = MOTION_PROXY_PREP_META[selectedMotionProxyPrepStatus]
    || MOTION_PROXY_PREP_META.preparing;
  const selectedMotionProxyDisplayError = firstText(selectedShot?.motionProxyError);

  const handlePrimaryKeyframeAction = () => {
    if (!selectedKeyframeUrl || selectedKeyframeBusy || isAssetActive(selectedKeyframeAsset)) return;
    ingestKeyframeAsset({
      targetJobId: job?.jobId,
      targetShotId: selectedShot.id,
      scope: selectedShotScope,
      url: selectedKeyframeUrl,
      name: `${selectedShot.shotKey || "镜头"}-关键帧`,
    });
  };

  const submitAnalysis = async () => {
    if (!sourceFile) {
      toast.warning("请先选择参考视频");
      return;
    }
    setSubmitting(true);
    try {
      const created = await analyzeVideoRepaint({
        file: sourceFile,
        projectId,
        analysisMode,
        transcript,
      });
      setJobs((previous) => [created, ...previous.filter((item) => item.jobId !== created.jobId)]);
      setMappingDirty(false);
      await loadJob(created.jobId, { quiet: true });
      setShowUploader(false);
      setSourceFile(null);
      toast.success("视频已提交分析，离开页面后也会继续运行");
    } catch (error) {
      toast.error(error.message || "提交分析失败", 0);
    } finally {
      setSubmitting(false);
    }
  };

  const retryAnalysis = async () => {
    const targetJobId = job?.jobId;
    if (!targetJobId || reanalyzingJobId) return;
    setReanalyzingJobId(targetJobId);
    try {
      await reanalyzeVideoRepaint(targetJobId);
      const refreshed = await loadJob(targetJobId, { quiet: true });
      if (refreshed) {
        toast.success("已使用原视频重新提交分析，无需再次上传");
      } else {
        toast.warning("重新分析已提交，任务状态暂未刷新，请稍后手动刷新");
      }
    } catch (error) {
      toast.error(error.message || "重新分析失败，请稍后重试", 0);
    } finally {
      setReanalyzingJobId((currentJobId) => (currentJobId === targetJobId ? null : currentJobId));
    }
  };

  const mutateReviewDraft = (updater, confirmationKey) => {
    if (!activeReviewDraft) return;
    setReviewDraft((previous) => {
      const base = reviewDirty && previous?.jobId === job?.jobId ? previous : activeReviewDraft;
      const updated = updater(base);
      return confirmationKey ? { ...updated, [confirmationKey]: false } : updated;
    });
    setReviewDirty(true);
  };

  const updateReviewDialogueLine = (index, patch) => {
    mutateReviewDraft((draft) => ({
      ...draft,
      dialogueLines: draft.dialogueLines.map((line, itemIndex) => (
        itemIndex === index
          ? {
            ...line,
            ...patch,
            verificationStatus: "MANUAL",
            requiresReview: false,
            reviewReason: "",
          }
          : line
      )),
    }), "dialogueConfirmed");
  };

  const setReviewDialogueConfirmed = (confirmed) => {
    if (confirmed) {
      const multilineIndex = activeReviewDraft?.dialogueLines?.findIndex(hasMultilineReviewDialogueText) ?? -1;
      if (multilineIndex >= 0) {
        toast.warning(`第 ${multilineIndex + 1} 句含有换行；一条原文必须只对应一条英文`);
        setReviewTab("dialogue");
        return;
      }
      const unresolvedIndex = activeReviewDraft?.dialogueLines?.findIndex(hasUnresolvedReviewDialogueLine) ?? -1;
      if (unresolvedIndex >= 0) {
        toast.warning(`第 ${unresolvedIndex + 1} 句仍有空值或说话人为 UNKNOWN，不能确认全片台词`);
        setReviewTab("dialogue");
        return;
      }
    }
    mutateReviewDraft((draft) => ({ ...draft, dialogueConfirmed: confirmed }));
  };

  const addReviewDialogueLine = () => {
    mutateReviewDraft((draft) => ({
      ...draft,
      dialogueLines: reindexDialogueLines([
        ...draft.dialogueLines,
        {
          lineId: nextReviewDraftId("REVIEW_LINE"),
          lineIndex: draft.dialogueLines.length,
          speakerCharacterKey: "UNKNOWN",
          shotKey: firstText(selectedShot?.shotKey, shots[0]?.shotKey),
          startSeconds: null,
          endSeconds: null,
          sourceText: "",
          englishText: "",
          verificationStatus: "MANUAL",
          requiresReview: false,
          evidenceSources: [],
          reviewReason: "",
        },
      ]),
    }), "dialogueConfirmed");
  };

  const deleteReviewDialogueLine = async (index) => {
    if (!(await appConfirm(`确定删除第 ${index + 1} 句台词吗？`))) return;
    mutateReviewDraft((draft) => ({
      ...draft,
      dialogueLines: reindexDialogueLines(draft.dialogueLines.filter((_, itemIndex) => itemIndex !== index)),
    }), "dialogueConfirmed");
  };

  const moveReviewDialogueLine = (index, offset) => {
    const targetIndex = index + offset;
    if (targetIndex < 0 || targetIndex >= (activeReviewDraft?.dialogueLines || []).length) return;
    mutateReviewDraft((draft) => {
      const lines = [...draft.dialogueLines];
      [lines[index], lines[targetIndex]] = [lines[targetIndex], lines[index]];
      return { ...draft, dialogueLines: reindexDialogueLines(lines) };
    }, "dialogueConfirmed");
  };

  const splitReviewDialogueLine = (index) => {
    const line = activeReviewDraft?.dialogueLines?.[index];
    if (!line) return;
    const [sourceFirst, sourceSecond] = splitReviewText(line.sourceText);
    const [englishFirst, englishSecond] = splitReviewText(line.englishText);
    if (!sourceSecond && !englishSecond) {
      toast.warning("当前台词太短，无法自动拆分");
      return;
    }
    const hasTimeRange = line.startSeconds !== null
      && line.endSeconds !== null
      && Number(line.endSeconds) > Number(line.startSeconds);
    const midpoint = hasTimeRange
      ? (Number(line.startSeconds) + Number(line.endSeconds)) / 2
      : null;
    mutateReviewDraft((draft) => {
      const current = draft.dialogueLines[index];
      const first = {
        ...current,
        sourceText: sourceFirst,
        englishText: englishFirst,
        endSeconds: midpoint ?? current.endSeconds,
        verificationStatus: "MANUAL",
        requiresReview: false,
        reviewReason: "",
      };
      const second = {
        ...current,
        lineId: nextReviewDraftId("REVIEW_LINE"),
        sourceText: sourceSecond,
        englishText: englishSecond,
        startSeconds: midpoint ?? current.startSeconds,
        verificationStatus: "MANUAL",
        requiresReview: false,
        reviewReason: "",
      };
      const lines = [...draft.dialogueLines];
      lines.splice(index, 1, first, second);
      return { ...draft, dialogueLines: reindexDialogueLines(lines) };
    }, "dialogueConfirmed");
  };

  const mergeNextReviewDialogueLine = (index) => {
    const current = activeReviewDraft?.dialogueLines?.[index];
    const next = activeReviewDraft?.dialogueLines?.[index + 1];
    if (!current || !next) return;
    if (current.shotKey !== next.shotKey || current.speakerCharacterKey !== next.speakerCharacterKey) {
      toast.warning("只有同一镜头、同一说话人的相邻台词可以合并");
      return;
    }
    mutateReviewDraft((draft) => {
      const first = draft.dialogueLines[index];
      const second = draft.dialogueLines[index + 1];
      const merged = {
        ...first,
        sourceText: joinReviewText(first.sourceText, second.sourceText),
        englishText: joinReviewText(first.englishText, second.englishText),
        endSeconds: second.endSeconds ?? first.endSeconds,
        verificationStatus: "MANUAL",
        requiresReview: false,
        evidenceSources: [...(first.evidenceSources || []), ...(second.evidenceSources || [])],
        reviewReason: "",
      };
      const lines = [...draft.dialogueLines];
      lines.splice(index, 2, merged);
      return { ...draft, dialogueLines: reindexDialogueLines(lines) };
    }, "dialogueConfirmed");
  };

  const addReviewRelationship = () => {
    const sourceCharacters = job?.characters || [];
    mutateReviewDraft((draft) => ({
      ...draft,
      relationships: [
        ...draft.relationships,
        {
          relationshipId: "",
          clientKey: nextReviewDraftId("RELATIONSHIP"),
          from: firstText(sourceCharacters[0]?.sourceKey),
          to: firstText(sourceCharacters[1]?.sourceKey),
          type: "UNKNOWN",
          evidence: "",
          evidenceLineIds: [],
          evidenceShotKeys: [],
          confidence: null,
          status: "UNKNOWN",
          requiresReview: false,
        },
      ],
    }), "relationshipsConfirmed");
  };

  const updateReviewRelationship = (index, patch) => {
    mutateReviewDraft((draft) => ({
      ...draft,
      relationships: draft.relationships.map((relationship, itemIndex) => (
        itemIndex === index ? { ...relationship, ...patch } : relationship
      )),
    }), "relationshipsConfirmed");
  };

  const deleteReviewRelationship = (index) => {
    mutateReviewDraft((draft) => ({
      ...draft,
      relationships: draft.relationships.filter((_, itemIndex) => itemIndex !== index),
    }), "relationshipsConfirmed");
  };

  const saveReview = async () => {
    if (!job?.jobId || !activeReviewDraft) return null;
    const multilineDialogueIndex = activeReviewDraft.dialogueLines.findIndex(hasMultilineReviewDialogueText);
    if (multilineDialogueIndex >= 0) {
      toast.warning(`第 ${multilineDialogueIndex + 1} 句的原文和英文必须各自保持为单独一行`);
      setReviewTab("dialogue");
      return null;
    }
    const invalidDialogueIndex = activeReviewDraft.dialogueConfirmed
      ? activeReviewDraft.dialogueLines.findIndex(hasUnresolvedReviewDialogueLine)
      : -1;
    if (invalidDialogueIndex >= 0) {
      toast.warning(`第 ${invalidDialogueIndex + 1} 句仍有空值或说话人为 UNKNOWN，不能确认全片台词`);
      setReviewTab("dialogue");
      return null;
    }
    const currentDialogueLineIds = new Set(activeReviewDraft.dialogueLines.map((line) => line.lineId));
    const sanitizedRelationships = activeReviewDraft.relationships.map((relationship) => ({
      ...relationship,
      evidenceLineIds: (relationship.evidenceLineIds || []).filter((lineId) => currentDialogueLineIds.has(lineId)),
      evidenceShotKeys: (relationship.evidenceShotKeys || []).filter((shotKey) => reviewShotKeys.has(shotKey)),
    }));
    const invalidRelationshipIndex = sanitizedRelationships.findIndex((relationship) => (
      !firstText(relationship.from)
      || !firstText(relationship.to)
      || relationship.from === relationship.to
      || !firstText(relationship.type)
    ));
    if (invalidRelationshipIndex >= 0) {
      toast.warning(`第 ${invalidRelationshipIndex + 1} 条人物关系需要补全不同的起点、终点和关系类型`);
      setReviewTab("relationships");
      return null;
    }
    const missingRelationshipEvidenceIndex = activeReviewDraft.relationshipsConfirmed
      ? sanitizedRelationships.findIndex((relationship) => (
        firstText(relationship.type).toUpperCase() !== "UNKNOWN"
        && !rawText(relationship.evidence).trim()
        && relationship.evidenceLineIds.length === 0
        && relationship.evidenceShotKeys.length === 0
      ))
      : -1;
    if (missingRelationshipEvidenceIndex >= 0) {
      toast.warning(`第 ${missingRelationshipEvidenceIndex + 1} 条非 UNKNOWN 关系缺少证据或备注`);
      setReviewTab("relationships");
      return null;
    }
    setSavingReview(true);
    try {
      const dialogueLines = reindexDialogueLines(activeReviewDraft.dialogueLines).map((line) => ({
        lineId: line.lineId,
        lineIndex: line.lineIndex,
        speakerCharacterKey: line.speakerCharacterKey,
        shotKey: line.shotKey,
        startSeconds: line.startSeconds,
        endSeconds: line.endSeconds,
        sourceText: rawText(line.sourceText),
        englishText: rawText(line.englishText),
        verificationStatus: line.verificationStatus,
        requiresReview: Boolean(line.requiresReview),
        evidenceSources: line.evidenceSources,
        reviewReason: rawText(line.reviewReason),
      }));
      const relationships = sanitizedRelationships.map((relationship) => ({
        fromCharacterKey: relationship.from,
        toCharacterKey: relationship.to,
        type: relationship.type,
        evidenceLineIds: relationship.evidenceLineIds,
        evidenceShotKeys: relationship.evidenceShotKeys,
        evidence: rawText(relationship.evidence),
        confidence: relationship.confidence,
        status: firstText(relationship.status, relationship.type === "UNKNOWN" ? "UNKNOWN" : "SUGGESTED"),
        requiresReview: Boolean(relationship.requiresReview),
      }));
      const updated = await updateVideoRepaintReview(job.jobId, {
        dialogueLines,
        dialogueConfirmed: Boolean(activeReviewDraft.dialogueConfirmed),
        relationships,
        relationshipsConfirmed: hasMultipleSourceCharacters
          ? Boolean(activeReviewDraft.relationshipsConfirmed)
          : true,
      });
      const refreshed = await loadJob(job.jobId, { quiet: true });
      const nextJob = refreshed || updated;
      if (nextJob) setJob(nextJob);
      setReviewDraft(createReviewDraft(nextJob));
      setReviewDirty(false);
      const refreshedShot = (nextJob?.shots || []).find((shot) => (
        String(shot.id) === String(selectedShot?.id)
      ));
      if (refreshedShot) {
        const refreshedDialogueLines = normalizeDialogueLines(refreshedShot);
        setShotDraft((previous) => previous ? {
          ...previous,
          dialogueLines: refreshedDialogueLines,
          englishDialogue: dialogueLinesToEnglishDialogue(refreshedDialogueLines),
        } : previous);
      }
      toast.success("台词与人物关系审核已保存");
      return nextJob;
    } catch (error) {
      toast.error(error.message || "保存台词与人物关系审核失败", 0);
      return null;
    } finally {
      setSavingReview(false);
    }
  };

  const saveMappings = async ({ quiet = false } = {}) => {
    if (!job?.jobId) return null;
    setSavingMappings(true);
    try {
      const updated = await updateVideoRepaintMappings(job.jobId, {
        characterTargets: Object.fromEntries(Object.entries(characterTargets).map(([key, value]) => [key, numericOrNull(value)])),
        sceneTargets: Object.fromEntries(Object.entries(sceneTargets).map(([key, value]) => [key, numericOrNull(value)])),
      });
      setJob(updated);
      setMappingDirty(false);
      if (!quiet) toast.success("人物与场景映射已保存");
      return updated;
    } catch (error) {
      toast.error(error.message || "保存映射失败", 0);
      return null;
    } finally {
      setSavingMappings(false);
    }
  };

  const saveSelectedShot = async ({ quiet = false } = {}) => {
    if (!job?.jobId || !selectedShot || !shotDraft) return null;
    setSavingShot(true);
    try {
      const targets = mappedTargetsForShot(selectedShot);
      const dialogueLines = Array.isArray(shotDraft.dialogueLines) ? shotDraft.dialogueLines : [];
      const structuredDialogueChanged = dialogueLinesRevision(dialogueLines)
        !== dialogueLinesRevision(normalizeDialogueLines(selectedShot));
      const legacyDialogueChanged = dialogueLines.length === 0
        && rawText(shotDraft.englishDialogue) !== rawText(selectedShot.englishDialogue);
      const payload = {
        generationStrategy: shotDraft.generationStrategy,
        prompt: shotDraft.prompt,
        targetCharacterIds: targets.targetCharacterIds.map(Number),
        targetSceneId: numericOrNull(targets.targetSceneId),
      };
      if (structuredDialogueChanged) {
        payload.dialogueLines = dialogueLines;
        payload.englishDialogue = dialogueLines.length > 0
          ? dialogueLinesToEnglishDialogue(dialogueLines)
          : shotDraft.englishDialogue;
      } else if (legacyDialogueChanged) {
        payload.englishDialogue = shotDraft.englishDialogue;
      }
      const updated = await updateVideoRepaintShot(job.jobId, selectedShot.id, payload);
      setJob(updated);
      if (!quiet) toast.success("当前镜头设置已保存");
      return updated;
    } catch (error) {
      toast.error(error.message || "保存镜头失败", 0);
      return null;
    } finally {
      setSavingShot(false);
    }
  };

  const generateSelectedShot = async () => {
    if (!selectedShot || !job?.jobId) return;
    if (selectedShotMissingMappingCount > 0) {
      toast.warning(`当前镜头还有 ${selectedShotMissingMappingCount} 项人物或场景没有映射`);
      return;
    }
    if (selectedGenerationBlockReason) {
      toast.warning(selectedGenerationBlockReason);
      return;
    }
    if (!(await appConfirm(`确定提交 ${selectedShot.shotKey} 到 Dreamina v3 生成英文转绘视频吗？`))) return;
    setGeneratingShotId(selectedShot.id);
    try {
      const mapped = await saveMappings({ quiet: true });
      if (!mapped) return;
      const saved = await saveSelectedShot({ quiet: true });
      if (!saved) return;
      const updated = await generateVideoRepaintShot(job.jobId, selectedShot.id);
      setJob(updated);
      toast.success("当前镜头已进入 Dreamina v3 生成队列");
    } catch (error) {
      toast.error(error.message || "镜头生成失败", 0);
    } finally {
      setGeneratingShotId(null);
    }
  };

  const generateAll = async () => {
    if (!job?.jobId) return;
    if (missingMappingCount > 0) {
      toast.warning(`还有 ${missingMappingCount} 项人物或场景没有映射`);
      return;
    }
    if (batchGenerationBlocks.length > 0) {
      toast.warning(`有 ${batchGenerationBlocks.length} 个镜头尚未满足 Dreamina v3 安全提交条件`);
      return;
    }
    const pendingCount = shots.filter((shot) => shot.status !== "processing" && shot.status !== "success").length;
    if (pendingCount === 0) {
      toast.info("当前没有需要生成的镜头");
      return;
    }
    if (!(await appConfirm(`确定将 ${pendingCount} 个未完成镜头提交到 Dreamina v3 吗？已完成和生成中的镜头不会重复提交。`))) return;
    setGeneratingAll(true);
    try {
      const mapped = await saveMappings({ quiet: true });
      if (!mapped) return;
      if (selectedShot && shotDraft) {
        const saved = await saveSelectedShot({ quiet: true });
        if (!saved) return;
      }
      const updated = await generateAllVideoRepaintShots(job.jobId);
      setJob(updated);
      const failed = Array.isArray(updated?.batchErrors) ? updated.batchErrors.length : 0;
      if (failed > 0) {
        toast.warning(`已提交 ${updated?.batchSubmitted || 0} 个镜头，${failed} 个镜头需检查映射或参考图`);
      } else {
        toast.success("未完成镜头已进入 Dreamina v3 生成队列");
      }
    } catch (error) {
      toast.error(error.message || "批量生成失败", 0);
    } finally {
      setGeneratingAll(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-full min-h-[520px] items-center justify-center gap-2" style={{ color: "var(--color-text-muted)" }}>
        <Loader2 size={18} className="animate-spin" /> 正在恢复转绘项目
      </div>
    );
  }

  const analyzing = job?.status === "analyzing";
  const completedCount = shots.filter((shot) => shot.status === "success").length;

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden" style={{ backgroundColor: "var(--color-bg-primary)" }}>
      <header className="flex min-h-14 shrink-0 items-center justify-between gap-4 border-b px-5" style={{ borderColor: "var(--color-border-primary)" }}>
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md" style={{ color: "var(--color-accent-primary)", backgroundColor: "color-mix(in srgb, var(--color-accent-primary) 12%, transparent)" }}>
            <Clapperboard size={17} />
          </div>
          <div className="min-w-0">
            <h1 className="truncate text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>影视转绘</h1>
            <p className="truncate text-[11px]" style={{ color: "var(--color-text-muted)" }}>锁定原片切点、动作与逐句台词，只替换目标人物、场景和语言</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {jobs.length > 0 && (
            <AppSelect
              value={job?.jobId || ""}
              onChange={(event) => loadJob(event.target.value)}
              className="h-8 max-w-52 rounded-md px-2 text-xs outline-none"
              style={{ color: "var(--color-text-secondary)", backgroundColor: "var(--color-bg-secondary)", border: "1px solid var(--color-border-primary)" }}
              title="历史分析"
            >
              {jobs.map((item, index) => <option key={item.jobId} value={item.jobId}>{`分析 ${jobs.length - index} · ${item.sourceFilename || item.jobId}`}</option>)}
            </AppSelect>
          )}
          <button
            type="button"
            onClick={() => setShowUploader(true)}
            className="inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-xs font-semibold"
            style={{ color: "var(--color-text-secondary)", border: "1px solid var(--color-border-primary)", backgroundColor: "var(--color-bg-secondary)" }}
          >
            <Upload size={14} /> 新分析
          </button>
          {job && (
            <button
              type="button"
              onClick={() => loadJob(job.jobId)}
              className="flex h-8 w-8 items-center justify-center rounded-md"
              style={{ color: "var(--color-text-secondary)", border: "1px solid var(--color-border-primary)", backgroundColor: "var(--color-bg-secondary)" }}
              title="刷新状态"
            >
              <RefreshCw size={14} />
            </button>
          )}
        </div>
      </header>

      {showUploader ? (
        <div className="flex flex-1 min-h-0 items-center justify-center overflow-y-auto p-6">
          <section className="w-full max-w-4xl rounded-lg border p-6" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-secondary)" }}>
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-base font-semibold" style={{ color: "var(--color-text-primary)" }}>导入参考视频</h2>
                <p className="mt-1 text-xs leading-5" style={{ color: "var(--color-text-muted)" }}>系统先识别人物、场景和镜头，不会在分析完成后自动生成视频。</p>
              </div>
              {job && <button type="button" onClick={() => setShowUploader(false)} className="text-xs" style={{ color: "var(--color-text-secondary)" }}>返回当前任务</button>}
            </div>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex h-40 w-full flex-col items-center justify-center gap-3 rounded-lg border border-dashed transition-colors"
              style={{ borderColor: sourceFile ? "var(--color-accent-primary)" : "var(--color-border-primary)", backgroundColor: "var(--color-bg-overlay)", color: "var(--color-text-secondary)" }}
            >
              <input ref={fileInputRef} type="file" accept="video/mp4,video/quicktime,video/webm" className="hidden" onChange={(event) => setSourceFile(event.target.files?.[0] || null)} />
              <Video size={26} strokeWidth={1.5} />
              <span className="text-sm font-medium">{sourceFile ? sourceFile.name : "选择中文参考视频"}</span>
              <span className="text-[11px]" style={{ color: "var(--color-text-muted)" }}>支持 MP4、MOV、WebM</span>
            </button>
            <div className="mt-5">
              <div className="mb-2 text-xs font-semibold" style={{ color: "var(--color-text-secondary)" }}>分析方式</div>
              <div className="grid grid-cols-3 gap-2">
                {ANALYSIS_MODES.map((item) => {
                  const active = analysisMode === item.value;
                  return (
                    <button
                      key={item.value}
                      type="button"
                      onClick={() => setAnalysisMode(item.value)}
                      className="rounded-md border p-3 text-left"
                      style={{ borderColor: active ? "var(--color-accent-primary)" : "var(--color-border-primary)", backgroundColor: active ? "color-mix(in srgb, var(--color-accent-primary) 9%, transparent)" : "var(--color-bg-overlay)" }}
                    >
                      <div className="text-xs font-semibold" style={{ color: active ? "var(--color-accent-primary)" : "var(--color-text-primary)" }}>{item.label}</div>
                      <div className="mt-1 text-[11px] leading-5" style={{ color: "var(--color-text-muted)" }}>{item.description}</div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="mt-5">
              <label htmlFor="video-repaint-source-transcript" className="mb-2 block text-xs font-semibold" style={{ color: "var(--color-text-secondary)" }}>原片完整对白或字幕（推荐）</label>
              <textarea
                id="video-repaint-source-transcript"
                value={transcript}
                onChange={(event) => setTranscript(event.target.value)}
                placeholder="请按原片顺序粘贴完整对白；系统会逐句翻译，不能省略或改写"
                className="h-28 w-full resize-none rounded-md p-3 text-xs leading-5 outline-none"
                style={{ color: "var(--color-text-primary)", backgroundColor: "var(--color-bg-overlay)", border: "1px solid var(--color-border-primary)" }}
              />
              <p className="mt-1.5 text-[10px] leading-4" style={{ color: "var(--color-text-muted)" }}>提供完整字幕能提高逐句切分准确度；每句英文会在镜头详情中与原文并排核对。</p>
            </div>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                disabled={submitting || !sourceFile}
                onClick={submitAnalysis}
                className="inline-flex h-10 items-center gap-2 rounded-md px-5 text-sm font-semibold disabled:opacity-50"
                style={{ color: "var(--color-text-on-accent)", backgroundColor: "var(--color-accent-primary)" }}
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <ScanSearch size={16} />}
                开始识别人景与镜头
              </button>
            </div>
          </section>
        </div>
      ) : !job ? null : analyzing ? (
        <div className="flex flex-1 min-h-0 items-center justify-center p-6">
          <div className="w-full max-w-2xl rounded-lg border p-6 text-center" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-secondary)" }}>
            <Loader2 size={28} className="mx-auto animate-spin" style={{ color: "var(--color-accent-primary)" }} />
            <h2 className="mt-4 text-base font-semibold" style={{ color: "var(--color-text-primary)" }}>{job.stageText || "正在分析视频"}</h2>
            <p className="mt-2 text-xs" style={{ color: "var(--color-text-muted)" }}>任务已保存，可以离开页面，回来后会自动恢复。</p>
            <div className="mt-5 h-1.5 overflow-hidden rounded-full" style={{ backgroundColor: "var(--color-bg-overlay)" }}>
              <div className="h-full transition-[width] duration-500" style={{ width: `${Math.max(4, Math.min(100, job.progress || 0))}%`, backgroundColor: "var(--color-accent-primary)" }} />
            </div>
            <div className="mt-2 text-[11px] font-mono-num" style={{ color: "var(--color-text-muted)" }}>{job.progress || 0}%</div>
          </div>
        </div>
      ) : job.status === "failed" && shots.length === 0 ? (
        <div className="flex flex-1 min-h-0 items-center justify-center p-6">
          <div className="w-full max-w-2xl rounded-lg border p-6 text-center" style={{ borderColor: "color-mix(in srgb, var(--color-error) 32%, transparent)", backgroundColor: "var(--color-bg-secondary)" }}>
            <AlertCircle size={30} className="mx-auto" style={{ color: "var(--color-error)" }} />
            <h2 className="mt-4 text-base font-semibold" style={{ color: "var(--color-text-primary)" }}>源片分析没有形成可用镜头</h2>
            <p className="mx-auto mt-2 max-w-xl text-xs leading-5" style={{ color: "var(--color-text-muted)" }}>{job.errorMessage || "人物、场景或镜头结构不完整，请重新分析。"}</p>
            <div className="mt-5 flex justify-center gap-2">
              <button type="button" onClick={() => loadJob(job.jobId)} className="inline-flex h-9 items-center gap-1.5 rounded-md border px-3 text-xs font-semibold" style={{ color: "var(--color-text-secondary)", borderColor: "var(--color-border-primary)" }}><RefreshCw size={14} />刷新状态</button>
              <button
                type="button"
                disabled={Boolean(reanalyzingJobId)}
                onClick={retryAnalysis}
                className="inline-flex h-9 items-center gap-1.5 rounded-md px-4 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-60"
                style={{ color: "var(--color-text-on-accent)", backgroundColor: "var(--color-accent-primary)" }}
              >
                {reanalyzingJobId === job.jobId ? <Loader2 size={14} className="animate-spin" /> : <ScanSearch size={14} />}
                {reanalyzingJobId === job.jobId ? "正在重新分析" : "使用原视频重新分析"}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="flex shrink-0 items-center justify-between gap-4 border-b px-5 py-2.5" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-secondary)" }}>
            <div className="flex items-center gap-6">
              {["源片分析", "人景映射", "逐镜生成"].map((label, index) => {
                const done = index === 0 || (index === 1 && missingMappingCount === 0) || (index === 2 && completedCount === shots.length && shots.length > 0);
                return (
                  <div key={label} className="flex items-center gap-2 text-xs" style={{ color: done ? "var(--color-success)" : "var(--color-text-muted)" }}>
                    <span className="flex h-5 w-5 items-center justify-center rounded-full border text-[10px]" style={{ borderColor: done ? "var(--color-success)" : "var(--color-border-primary)" }}>{done ? <Check size={12} /> : index + 1}</span>
                    {label}
                    {index < 2 && <ArrowRight size={12} style={{ color: "var(--color-border-secondary)" }} />}
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-3 text-[11px]" style={{ color: "var(--color-text-muted)" }}>
              <span>{(job.characters || []).length} 人</span>
              <span>{(job.scenes || []).length} 场景</span>
              <span>{completedCount}/{shots.length} 镜头完成</span>
              <StatusBadge status={job.status} />
            </div>
          </div>

          {job.status === "failed" && (
            <div className="mx-5 mt-3 flex items-start gap-2 rounded-md border p-3 text-xs" style={{ color: "var(--color-error)", borderColor: "color-mix(in srgb, var(--color-error) 30%, transparent)", backgroundColor: "color-mix(in srgb, var(--color-error) 7%, transparent)" }}>
              <AlertCircle size={15} className="mt-0.5 shrink-0" /> {job.errorMessage || "分析失败，请重新提交视频"}
            </div>
          )}

          <main className="grid min-h-0 flex-1 grid-cols-[280px_minmax(260px,0.75fr)_minmax(420px,1.25fr)] gap-3 overflow-hidden p-3">
            <section className="flex min-h-0 flex-col overflow-hidden rounded-lg border" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-secondary)" }}>
              <div className="border-b p-3" style={{ borderColor: "var(--color-border-primary)" }}>
                <div className="flex items-center justify-between">
                  <div className="text-xs font-semibold" style={{ color: "var(--color-text-primary)" }}>目标资产映射</div>
                  <span className="text-[11px]" style={{ color: missingMappingCount ? "var(--color-warning)" : "var(--color-success)" }}>{missingMappingCount ? `缺 ${missingMappingCount} 项` : "已完成"}</span>
                </div>
                <div className="mt-3 grid grid-cols-2 rounded-md p-0.5" style={{ backgroundColor: "var(--color-bg-overlay)" }}>
                  <button type="button" onClick={() => setMappingTab("characters")} className="h-7 rounded text-xs" style={{ color: mappingTab === "characters" ? "var(--color-text-primary)" : "var(--color-text-muted)", backgroundColor: mappingTab === "characters" ? "var(--color-bg-tertiary)" : "transparent" }}><Users size={13} className="mr-1 inline" />人物</button>
                  <button type="button" onClick={() => setMappingTab("scenes")} className="h-7 rounded text-xs" style={{ color: mappingTab === "scenes" ? "var(--color-text-primary)" : "var(--color-text-muted)", backgroundColor: mappingTab === "scenes" ? "var(--color-bg-tertiary)" : "transparent" }}><MapPin size={13} className="mr-1 inline" />场景</button>
                </div>
              </div>
              <div className="custom-scrollbar flex-1 overflow-y-auto p-2">
                {(mappingTab === "characters" ? job.characters || [] : job.scenes || []).map((item) => {
                  const isCharacter = mappingTab === "characters";
                  const value = isCharacter ? characterTargets[item.sourceKey] || "" : sceneTargets[item.sourceKey] || "";
                  const options = (isCharacter ? job.workshopCharacters || [] : job.workshopScenes || [])
                    .filter((option) => !option.sourcePlaceholder);
                  return (
                    <div key={item.sourceKey} className="mb-2 rounded-md border p-2" style={{ borderColor: value ? "var(--color-border-primary)" : "color-mix(in srgb, var(--color-warning) 36%, transparent)", backgroundColor: "var(--color-bg-overlay)" }}>
                      <div className="flex gap-2">
                        <div className="h-14 w-14 shrink-0 overflow-hidden rounded-md" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
                          {item.representativeFrameUrl ? <img src={item.representativeFrameUrl} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" /> : <EmptyImage label="" icon={isCharacter ? Users : ImageIcon} />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-xs font-semibold" style={{ color: "var(--color-text-primary)" }}>{item.sourceName || item.sourceKey}</div>
                          <div className="mt-1 line-clamp-2 text-[10px] leading-4" style={{ color: "var(--color-text-muted)" }}>{item.description || "待确认目标形象"}</div>
                        </div>
                      </div>
                      <AppSelect
                        value={value}
                        onChange={(event) => {
                          setMappingDirty(true);
                          if (isCharacter) {
                            setCharacterTargets((previous) => ({ ...previous, [item.sourceKey]: event.target.value }));
                          } else {
                            setSceneTargets((previous) => ({ ...previous, [item.sourceKey]: event.target.value }));
                          }
                        }}
                        className="mt-2 h-8 w-full rounded-md px-2 text-[11px] outline-none"
                        style={{ color: value ? "var(--color-text-primary)" : "var(--color-warning)", backgroundColor: "var(--color-bg-secondary)", border: "1px solid var(--color-border-primary)" }}
                      >
                        <option value="">选择目标{isCharacter ? "角色" : "场景"}</option>
                        {options.map((option) => <option key={option.id} value={option.id}>{option.name}</option>)}
                      </AppSelect>
                      {options.length === 0 && (
                        <p className="mt-1.5 text-[10px] leading-4" style={{ color: "var(--color-warning)" }}>
                          请先到{isCharacter ? "角色工坊" : "场景工坊"}创建并确认英文成片使用的目标资产。
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="border-t p-2" style={{ borderColor: "var(--color-border-primary)" }}>
                <div className="mb-2 grid grid-cols-2 gap-1.5">
                  <button
                    type="button"
                    onClick={() => navigate(`/project/${projectId}/assets/character`)}
                    className="inline-flex h-8 items-center justify-center gap-1 rounded-md border text-[11px]"
                    style={{ color: "var(--color-text-secondary)", borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-overlay)" }}
                  >
                    <Users size={12} />角色工坊
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(`/project/${projectId}/assets/scene`)}
                    className="inline-flex h-8 items-center justify-center gap-1 rounded-md border text-[11px]"
                    style={{ color: "var(--color-text-secondary)", borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-overlay)" }}
                  >
                    <MapPin size={12} />场景工坊
                  </button>
                </div>
                <button type="button" disabled={savingMappings} onClick={() => saveMappings()} className="inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-md text-xs font-semibold disabled:opacity-50" style={{ color: "var(--color-text-on-accent)", backgroundColor: "var(--color-accent-primary)" }}>
                  {savingMappings ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} 保存映射
                </button>
              </div>
            </section>

            <section className="flex min-h-0 flex-col overflow-hidden rounded-lg border" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-secondary)" }}>
              <div className="flex items-center justify-between border-b p-3" style={{ borderColor: "var(--color-border-primary)" }}>
                <div>
                  <div className="text-xs font-semibold" style={{ color: "var(--color-text-primary)" }}>镜头列表</div>
                  <div className="mt-0.5 max-w-52 truncate text-[10px]" title={batchGenerationBlocks[0]?.reason || ""} style={{ color: batchGenerationBlocks.length > 0 ? "var(--color-warning)" : "var(--color-text-muted)" }}>
                    {batchGenerationBlocks.length > 0
                      ? `${batchGenerationBlocks.length} 个镜头待处理：${batchGenerationBlocks[0].reason}`
                      : "点击镜头核对人物、场景和英文对白"}
                  </div>
                </div>
                <button
                  type="button"
                  disabled={generatingAll || missingMappingCount > 0 || batchGenerationBlocks.length > 0 || shots.length === 0}
                  onClick={generateAll}
                  aria-label={batchGenerationBlocks.length > 0 ? `${batchGenerationBlocks.length} 个镜头尚未满足安全提交条件` : "生成未完成镜头"}
                  title={batchGenerationBlocks.length > 0 ? batchGenerationBlocks[0].reason : "生成未完成镜头"}
                  className="inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-[11px] font-semibold disabled:opacity-40"
                  style={{ color: "var(--color-text-on-accent)", backgroundColor: "var(--color-accent-primary)" }}
                >
                  {generatingAll ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} 生成未完成镜头
                </button>
              </div>
              <div className="custom-scrollbar flex-1 overflow-y-auto p-2">
                {shots.map((shot) => {
                  const active = String(shot.id) === String(selectedShot?.id);
                  return (
                    <button
                      key={shot.id}
                      type="button"
                      onClick={() => setSelectedShotId(shot.id)}
                      className="mb-2 flex w-full gap-2 rounded-md border p-2 text-left transition-colors"
                      style={{ borderColor: active ? "var(--color-accent-primary)" : "var(--color-border-primary)", backgroundColor: active ? "color-mix(in srgb, var(--color-accent-primary) 8%, transparent)" : "var(--color-bg-overlay)" }}
                    >
                      <div className="h-16 w-24 shrink-0 overflow-hidden rounded-md" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
                        {shot.referenceFrameUrls?.[0] ? <img src={shot.referenceFrameUrls[0]} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" /> : <EmptyImage label="" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[11px] font-semibold" style={{ color: "var(--color-text-primary)" }}>{shot.shotKey}</span>
                          <StatusBadge status={shot.status} />
                        </div>
                        <div className="mt-1 line-clamp-2 text-[10px] leading-4" style={{ color: "var(--color-text-muted)" }}>{shot.title || shot.visualSummary}</div>
                        <div className="mt-1 text-[10px] font-mono-num" style={{ color: "var(--color-text-muted)" }}>{shot.timeRange || `${shot.startSeconds || 0}s - ${shot.endSeconds || 0}s`}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="custom-scrollbar min-h-0 overflow-y-auto rounded-lg border p-3" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-secondary)" }}>
              {!selectedShot || !shotDraft ? <EmptyImage label="选择一个镜头" icon={Film} /> : (
                <div className="space-y-3">
                  <section className="overflow-hidden rounded-md border" style={{ borderColor: reviewBlockingReason ? "color-mix(in srgb, var(--color-warning) 36%, var(--color-border-primary))" : "var(--color-border-primary)", backgroundColor: "var(--color-bg-overlay)" }}>
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b px-3 py-2.5" style={{ borderColor: "var(--color-border-primary)" }}>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h2 className="text-xs font-semibold" style={{ color: "var(--color-text-primary)" }}>台词与人物关系核对</h2>
                          {activeReviewDraft?.reviewRequired && (
                            <span className="rounded px-1.5 py-0.5 text-[10px] font-semibold" style={{ color: "var(--color-warning)", backgroundColor: "color-mix(in srgb, var(--color-warning) 10%, transparent)", border: "1px solid color-mix(in srgb, var(--color-warning) 28%, transparent)" }}>需要审核</span>
                          )}
                          {!reviewBlockingReason && (
                            <span className="rounded px-1.5 py-0.5 text-[10px] font-semibold" style={{ color: "var(--color-success)", backgroundColor: "color-mix(in srgb, var(--color-success) 10%, transparent)", border: "1px solid color-mix(in srgb, var(--color-success) 28%, transparent)" }}>已确认</span>
                          )}
                        </div>
                        <p className="mt-1 text-[10px] leading-4" style={{ color: reviewBlockingReason ? "var(--color-warning)" : "var(--color-text-muted)" }}>
                          {reviewBlockingReason || `全片 ${(activeReviewDraft?.dialogueLines || []).length} 句台词 · ${(activeReviewDraft?.relationships || []).length} 条人物关系`}
                        </p>
                      </div>
                      <button
                        type="button"
                        disabled={savingReview || !reviewHasUnsavedChanges}
                        onClick={saveReview}
                        className="inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-[11px] font-semibold disabled:opacity-45"
                        style={{ color: "var(--color-text-on-accent)", backgroundColor: "var(--color-accent-primary)" }}
                      >
                        {savingReview ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                        保存审核
                      </button>
                    </div>
                    <div className="grid grid-cols-2 border-b p-1" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-secondary)" }}>
                      <button
                        type="button"
                        onClick={() => setReviewTab("dialogue")}
                        className="h-8 rounded text-[11px] font-semibold transition-colors"
                        style={{ color: reviewTab === "dialogue" ? "var(--color-text-primary)" : "var(--color-text-muted)", backgroundColor: reviewTab === "dialogue" ? "var(--color-bg-tertiary)" : "transparent" }}
                      >
                        全片台词（{(activeReviewDraft?.dialogueLines || []).length}）
                      </button>
                      <button
                        type="button"
                        onClick={() => setReviewTab("relationships")}
                        className="h-8 rounded text-[11px] font-semibold transition-colors"
                        style={{ color: reviewTab === "relationships" ? "var(--color-text-primary)" : "var(--color-text-muted)", backgroundColor: reviewTab === "relationships" ? "var(--color-bg-tertiary)" : "transparent" }}
                      >
                        人物关系（{(activeReviewDraft?.relationships || []).length}）
                      </button>
                    </div>

                    {reviewTab === "dialogue" ? (
                      <div className="custom-scrollbar max-h-[520px] overflow-y-auto p-2.5">
                        <div className="mb-2 flex items-center justify-between gap-3">
                          <p className="text-[10px] leading-4" style={{ color: "var(--color-text-muted)" }}>按原片顺序核对每一句；可修正原文、翻译、说话人和所属镜头。</p>
                          <button type="button" onClick={addReviewDialogueLine} className="inline-flex h-8 shrink-0 items-center gap-1 rounded-md border px-2.5 text-[11px] font-semibold" style={{ color: "var(--color-text-secondary)", borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-secondary)" }}>
                            <Plus size={13} />补一行
                          </button>
                        </div>
                        {(activeReviewDraft?.dialogueLines || []).length === 0 ? (
                          <div className="rounded-md border px-3 py-5 text-center text-[11px]" style={{ color: "var(--color-text-muted)", borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-secondary)" }}>原片没有对白时，可直接勾选下方“确认原片无对白”。</div>
                        ) : (
                          <div className="space-y-2">
                            {(activeReviewDraft?.dialogueLines || []).map((line, index) => {
                              const verificationMeta = reviewVerificationMeta(line.verificationStatus, line.requiresReview);
                              const evidenceSummary = reviewEvidenceSummary(line);
                              return (
                                <div key={line.lineId || index} className="rounded-md border p-2.5" style={{ borderColor: line.requiresReview ? "color-mix(in srgb, var(--color-warning) 38%, var(--color-border-primary))" : "var(--color-border-primary)", backgroundColor: "var(--color-bg-secondary)" }}>
                                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                                    <div className="flex flex-wrap items-center gap-1.5 text-[10px]" style={{ color: "var(--color-text-muted)" }}>
                                      <span className="font-mono-num">#{index + 1}</span>
                                      <span>{formatTimestamp(line.startSeconds) || "--:--"} - {formatTimestamp(line.endSeconds) || "--:--"}</span>
                                      <span className="rounded px-1.5 py-0.5 font-semibold" style={{ color: verificationMeta.color, backgroundColor: `color-mix(in srgb, ${verificationMeta.color} 10%, transparent)`, border: `1px solid color-mix(in srgb, ${verificationMeta.color} 25%, transparent)` }}>{verificationMeta.label}</span>
                                      {line.requiresReview && <span style={{ color: "var(--color-warning)" }}>requiresReview</span>}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-1">
                                      <button type="button" disabled={index === 0} onClick={() => moveReviewDialogueLine(index, -1)} className="flex h-7 w-7 items-center justify-center rounded border disabled:opacity-35" style={{ color: "var(--color-text-secondary)", borderColor: "var(--color-border-primary)" }} aria-label={`上移第 ${index + 1} 句`} title="上移"><ArrowUp size={12} /></button>
                                      <button type="button" disabled={index >= activeReviewDraft.dialogueLines.length - 1} onClick={() => moveReviewDialogueLine(index, 1)} className="flex h-7 w-7 items-center justify-center rounded border disabled:opacity-35" style={{ color: "var(--color-text-secondary)", borderColor: "var(--color-border-primary)" }} aria-label={`下移第 ${index + 1} 句`} title="下移"><ArrowDown size={12} /></button>
                                      <button type="button" onClick={() => splitReviewDialogueLine(index)} className="inline-flex h-7 items-center gap-1 rounded border px-2 text-[10px]" style={{ color: "var(--color-text-secondary)", borderColor: "var(--color-border-primary)" }} title="自动从文本中间附近拆分"><Scissors size={11} />拆分</button>
                                      <button type="button" disabled={index >= activeReviewDraft.dialogueLines.length - 1} onClick={() => mergeNextReviewDialogueLine(index)} className="h-7 rounded border px-2 text-[10px] disabled:opacity-35" style={{ color: "var(--color-text-secondary)", borderColor: "var(--color-border-primary)" }}>合并下一行</button>
                                      <button type="button" onClick={() => deleteReviewDialogueLine(index)} className="flex h-7 w-7 items-center justify-center rounded border" style={{ color: "var(--color-error)", borderColor: "color-mix(in srgb, var(--color-error) 30%, var(--color-border-primary))" }} aria-label={`删除第 ${index + 1} 句`}><Trash2 size={12} /></button>
                                    </div>
                                  </div>
                                  {evidenceSummary && <div className="mb-2 rounded border px-2 py-1.5 text-[10px] leading-4" style={{ color: line.requiresReview ? "var(--color-warning)" : "var(--color-text-muted)", borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-overlay)" }}>{evidenceSummary}</div>}
                                  <div className="mb-2 grid grid-cols-2 gap-2">
                                    <label className="text-[10px]" style={{ color: "var(--color-text-muted)" }}>
                                      说话人
                                      <AppSelect value={line.speakerCharacterKey || ""} onChange={(event) => updateReviewDialogueLine(index, { speakerCharacterKey: event.target.value })} className="mt-1 h-8 w-full rounded-md px-2 text-[11px] outline-none" style={{ color: "var(--color-text-primary)", backgroundColor: "var(--color-bg-overlay)", border: "1px solid var(--color-border-primary)" }}>
                                        <option value="">选择说话人</option>
                                        {!reviewSpeakerKeys.has(line.speakerCharacterKey) && line.speakerCharacterKey && <option value={line.speakerCharacterKey}>{line.speakerCharacterKey}（原值）</option>}
                                        {(job.characters || []).map((character) => <option key={character.sourceKey} value={character.sourceKey}>{character.sourceName || character.sourceKey}（{character.sourceKey}）</option>)}
                                        {REVIEW_SPECIAL_SPEAKERS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                                      </AppSelect>
                                    </label>
                                    <label className="text-[10px]" style={{ color: "var(--color-text-muted)" }}>
                                      所属镜头
                                      <AppSelect value={line.shotKey || ""} onChange={(event) => updateReviewDialogueLine(index, { shotKey: event.target.value })} className="mt-1 h-8 w-full rounded-md px-2 text-[11px] outline-none" style={{ color: "var(--color-text-primary)", backgroundColor: "var(--color-bg-overlay)", border: "1px solid var(--color-border-primary)" }}>
                                        <option value="">选择镜头</option>
                                        {!reviewShotKeys.has(line.shotKey) && line.shotKey && <option value={line.shotKey}>{line.shotKey}（原值）</option>}
                                        {shots.map((shot) => <option key={shot.shotKey} value={shot.shotKey}>{shot.shotKey} · {shot.timeRange || shot.title || "未命名"}</option>)}
                                      </AppSelect>
                                    </label>
                                  </div>
                                  <div className="grid grid-cols-2 gap-2">
                                    <label className="text-[10px]" style={{ color: "var(--color-text-muted)" }}>
                                      原片原文
                                      <textarea value={line.sourceText} onChange={(event) => updateReviewDialogueLine(index, { sourceText: event.target.value })} className="mt-1 min-h-20 w-full resize-y rounded-md p-2 text-xs leading-5 outline-none" style={{ color: "var(--color-text-primary)", backgroundColor: "var(--color-bg-overlay)", border: "1px solid var(--color-border-primary)" }} />
                                    </label>
                                    <label className="text-[10px]" style={{ color: "var(--color-text-muted)" }}>
                                      英文 1:1 翻译
                                      <textarea value={line.englishText} onChange={(event) => updateReviewDialogueLine(index, { englishText: event.target.value })} className="mt-1 min-h-20 w-full resize-y rounded-md p-2 text-xs leading-5 outline-none" style={{ color: "var(--color-text-primary)", backgroundColor: "var(--color-bg-overlay)", border: "1px solid var(--color-border-primary)" }} />
                                    </label>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        <label className="mt-3 flex cursor-pointer items-start gap-2 rounded-md border p-2.5 text-[11px]" style={{ color: "var(--color-text-secondary)", borderColor: activeReviewDraft?.dialogueConfirmed ? "color-mix(in srgb, var(--color-success) 38%, var(--color-border-primary))" : "var(--color-border-primary)", backgroundColor: "var(--color-bg-secondary)" }}>
                          <input type="checkbox" checked={Boolean(activeReviewDraft?.dialogueConfirmed)} onChange={(event) => setReviewDialogueConfirmed(event.target.checked)} className="mt-0.5" />
                          <span>{(activeReviewDraft?.dialogueLines || []).length === 0 ? "确认原片无对白" : "确认全片台词已逐句覆盖，原文、英文、说话人与所属镜头均正确"}</span>
                        </label>
                      </div>
                    ) : (
                      <div className="custom-scrollbar max-h-[520px] overflow-y-auto p-2.5">
                        <datalist id="video-repaint-relationship-types">
                          {RELATIONSHIP_TYPE_SUGGESTIONS.map((type) => <option key={type} value={type} />)}
                        </datalist>
                        <div className="mb-2 flex items-start justify-between gap-3">
                          <p className="text-[10px] leading-4" style={{ color: "var(--color-text-muted)" }}>核对原片人物之间的关系；无法判断时将关系类型填写为 <span className="font-mono">UNKNOWN</span>，仍可人工确认。</p>
                          <button type="button" disabled={(job.characters || []).length < 2} onClick={addReviewRelationship} className="inline-flex h-8 shrink-0 items-center gap-1 rounded-md border px-2.5 text-[11px] font-semibold disabled:opacity-40" style={{ color: "var(--color-text-secondary)", borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-secondary)" }}><Plus size={13} />新增关系</button>
                        </div>
                        {(activeReviewDraft?.relationships || []).length === 0 ? (
                          <div className="rounded-md border px-3 py-5 text-center text-[11px]" style={{ color: "var(--color-text-muted)", borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-secondary)" }}>{hasMultipleSourceCharacters ? "尚无可靠人物关系证据；可以新增 UNKNOWN 关系，也可以直接确认未识别出可靠关系。" : "源片不足两名人物，无需填写多人关系。"}</div>
                        ) : (
                          <div className="space-y-2">
                            {(activeReviewDraft?.relationships || []).map((relationship, index) => (
                              <div key={relationship.clientKey || relationship.relationshipId || index} className="rounded-md border p-2.5" style={{ borderColor: relationship.requiresReview ? "color-mix(in srgb, var(--color-warning) 38%, var(--color-border-primary))" : "var(--color-border-primary)", backgroundColor: "var(--color-bg-secondary)" }}>
                                <div className="mb-2 flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-2 text-[10px]" style={{ color: "var(--color-text-muted)" }}>
                                    <span>关系 #{index + 1}</span>
                                    <span>置信度 {formatConfidence(relationship.confidence)}</span>
                                    {relationship.requiresReview && <span style={{ color: "var(--color-warning)" }}>需核对</span>}
                                  </div>
                                  <button type="button" onClick={() => deleteReviewRelationship(index)} className="flex h-7 w-7 items-center justify-center rounded border" style={{ color: "var(--color-error)", borderColor: "color-mix(in srgb, var(--color-error) 30%, var(--color-border-primary))" }} aria-label={`删除第 ${index + 1} 条关系`}><Trash2 size={12} /></button>
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                  <label className="text-[10px]" style={{ color: "var(--color-text-muted)" }}>
                                    From
                                    <AppSelect value={relationship.from || ""} onChange={(event) => updateReviewRelationship(index, { from: event.target.value })} className="mt-1 h-8 w-full rounded-md px-2 text-[11px] outline-none" style={{ color: "var(--color-text-primary)", backgroundColor: "var(--color-bg-overlay)", border: "1px solid var(--color-border-primary)" }}><option value="">选择人物</option>{(job.characters || []).map((character) => <option key={character.sourceKey} value={character.sourceKey}>{character.sourceName || character.sourceKey}</option>)}</AppSelect>
                                  </label>
                                  <label className="text-[10px]" style={{ color: "var(--color-text-muted)" }}>
                                    To
                                    <AppSelect value={relationship.to || ""} onChange={(event) => updateReviewRelationship(index, { to: event.target.value })} className="mt-1 h-8 w-full rounded-md px-2 text-[11px] outline-none" style={{ color: "var(--color-text-primary)", backgroundColor: "var(--color-bg-overlay)", border: "1px solid var(--color-border-primary)" }}><option value="">选择人物</option>{(job.characters || []).map((character) => <option key={character.sourceKey} value={character.sourceKey}>{character.sourceName || character.sourceKey}</option>)}</AppSelect>
                                  </label>
                                  <label className="text-[10px]" style={{ color: "var(--color-text-muted)" }}>
                                    Type
                                    <input list="video-repaint-relationship-types" value={relationship.type || ""} onChange={(event) => updateReviewRelationship(index, { type: event.target.value })} placeholder="UNKNOWN" className="mt-1 h-8 w-full rounded-md px-2 text-[11px] outline-none" style={{ color: "var(--color-text-primary)", backgroundColor: "var(--color-bg-overlay)", border: "1px solid var(--color-border-primary)" }} />
                                  </label>
                                </div>
                                <label className="mt-2 block text-[10px]" style={{ color: "var(--color-text-muted)" }}>
                                  证据 / 人工备注
                                  <textarea
                                    value={relationship.evidence || ""}
                                    onChange={(event) => updateReviewRelationship(index, { evidence: event.target.value })}
                                    placeholder={relationship.type === "UNKNOWN" ? "UNKNOWN 可留空" : "填写支持该关系的对白、动作或人工判断依据"}
                                    className="mt-1 min-h-16 w-full resize-y rounded-md p-2 text-[11px] leading-5 outline-none"
                                    style={{ color: "var(--color-text-primary)", backgroundColor: "var(--color-bg-overlay)", border: "1px solid var(--color-border-primary)" }}
                                  />
                                </label>
                                {(relationship.evidenceLineIds.length > 0 || relationship.evidenceShotKeys.length > 0) && (
                                  <div className="mt-2 rounded-md border px-2.5 py-2 text-[10px] leading-4" style={{ color: "var(--color-text-muted)", borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-overlay)" }}>
                                    <span className="font-semibold">结构化证据：</span>
                                    {[...relationship.evidenceLineIds, ...relationship.evidenceShotKeys].join(" · ")}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        {hasMultipleSourceCharacters ? (
                          <label className="mt-3 flex cursor-pointer items-start gap-2 rounded-md border p-2.5 text-[11px]" style={{ color: "var(--color-text-secondary)", borderColor: activeReviewDraft?.relationshipsConfirmed ? "color-mix(in srgb, var(--color-success) 38%, var(--color-border-primary))" : "var(--color-border-primary)", backgroundColor: "var(--color-bg-secondary)" }}>
                            <input type="checkbox" checked={Boolean(activeReviewDraft?.relationshipsConfirmed)} onChange={(event) => mutateReviewDraft((draft) => ({ ...draft, relationshipsConfirmed: event.target.checked }))} className="mt-0.5" />
                            <span>{(activeReviewDraft?.relationships || []).length > 0 ? "确认多人关系已核对；未知关系已明确填写为 UNKNOWN" : "确认原片未识别出可靠人物关系，不让模型自行猜测"}</span>
                          </label>
                        ) : (
                          <div className="mt-3 rounded-md border p-2.5 text-[11px]" style={{ color: "var(--color-success)", borderColor: "color-mix(in srgb, var(--color-success) 30%, var(--color-border-primary))", backgroundColor: "var(--color-bg-secondary)" }}>当前不足两名源人物，不阻断生成。</div>
                        )}
                      </div>
                    )}
                  </section>

                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>{selectedShot.shotKey} · {selectedShot.title || "未命名镜头"}</h2>
                        <StatusBadge status={selectedShot.status} />
                      </div>
                      <p className="mt-1 text-[11px]" style={{ color: "var(--color-text-muted)" }}>{selectedShot.timeRange} · {selectedShot.durationSeconds || 0} 秒</p>
                    </div>
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      {selectedGenerationBlockReason && (
                        <span className="max-w-56 text-right text-[10px] leading-4" style={{ color: "var(--color-warning)" }}>
                          {selectedGenerationBlockReason}
                        </span>
                      )}
                      <button type="button" disabled={savingShot} onClick={() => saveSelectedShot()} className="inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-[11px] font-semibold disabled:opacity-50" style={{ color: "var(--color-text-secondary)", borderColor: "var(--color-border-primary)" }}><Save size={13} />保存</button>
                      <button
                        type="button"
                        disabled={generatingShotId === selectedShot.id || selectedShot.status === "processing" || Boolean(selectedGenerationBlockReason)}
                        onClick={generateSelectedShot}
                        aria-label={selectedGenerationBlockReason || "生成当前镜头"}
                        title={selectedGenerationBlockReason || "生成当前镜头"}
                        className="inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-[11px] font-semibold disabled:opacity-50"
                        style={{ color: "var(--color-text-on-accent)", backgroundColor: "var(--color-accent-primary)" }}
                      >
                        {generatingShotId === selectedShot.id || selectedShot.status === "processing" ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}生成当前镜头
                      </button>
                    </div>
                  </div>

                  <div>
                    <div className="mb-2 text-[11px] font-semibold" style={{ color: "var(--color-text-secondary)" }}>生成方式</div>
                    <div className="inline-flex rounded-md p-0.5" style={{ backgroundColor: "var(--color-bg-overlay)" }}>
                      {GENERATION_MODES.map((mode) => (
                        <button
                          key={mode.value}
                          type="button"
                          onClick={() => setShotDraft((previous) => ({ ...previous, generationStrategy: mode.value }))}
                          className="h-8 rounded px-3 text-[11px] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                          style={{
                            color: shotDraft.generationStrategy === mode.value ? "var(--color-text-primary)" : "var(--color-text-muted)",
                            backgroundColor: shotDraft.generationStrategy === mode.value ? "var(--color-bg-tertiary)" : "transparent",
                            outlineColor: "var(--color-accent-primary)",
                          }}
                        >
                          {mode.label}
                        </button>
                      ))}
                    </div>
                    <p className="mt-1.5 text-[10px] leading-4" style={{ color: "var(--color-text-muted)" }}>
                      {shotDraft.generationStrategy === "motion_proxy"
                        ? "使用去除人脸与纹理细节的临时代理视频，优先复刻原片动作、构图和运镜。"
                        : "使用关键帧和原片分析提示词重绘，不直接提交原视频。"}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="overflow-hidden rounded-md border" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-overlay)" }}>
                      <div className="border-b px-3 py-2 text-[11px] font-semibold" style={{ color: "var(--color-text-secondary)", borderColor: "var(--color-border-primary)" }}>原片镜头</div>
                      <div className="aspect-video">
                        {selectedShot.sourceClipUrl ? <video src={selectedShot.sourceClipUrl} controls preload="metadata" className="h-full w-full bg-black object-contain" /> : selectedShot.referenceFrameUrls?.[0] ? <img src={selectedShot.referenceFrameUrls[0]} alt="" className="h-full w-full object-contain" /> : <EmptyImage label="正在准备原片段" icon={Video} />}
                      </div>
                    </div>
                    <div className="overflow-hidden rounded-md border" style={{ borderColor: selectedShot.resultVideoUrl ? "color-mix(in srgb, var(--color-success) 45%, transparent)" : "var(--color-border-primary)", backgroundColor: "var(--color-bg-overlay)" }}>
                      <div className="border-b px-3 py-2 text-[11px] font-semibold" style={{ color: "var(--color-text-secondary)", borderColor: "var(--color-border-primary)" }}>英文转绘结果</div>
                      <div className="aspect-video">
                        {selectedShot.resultVideoUrl ? <video src={selectedShot.resultVideoUrl} controls preload="metadata" className="h-full w-full bg-black object-contain" /> : <EmptyImage label={selectedShot.status === "processing" ? "Dreamina v3 生成中" : "尚未生成"} icon={Clapperboard} />}
                      </div>
                    </div>
                  </div>

                  {shotDraft.generationStrategy === "motion_proxy" && (
                    <div className="overflow-hidden rounded-md border" style={{ borderColor: `color-mix(in srgb, ${selectedMotionProxyPrepMeta.color} 38%, var(--color-border-primary))`, backgroundColor: "var(--color-bg-overlay)" }}>
                      <div className="flex items-center justify-between gap-3 border-b px-3 py-2" style={{ borderColor: "var(--color-border-primary)" }}>
                        <div className="flex items-center gap-2 text-[11px] font-semibold" style={{ color: "var(--color-text-secondary)" }}>
                          <Video size={13} /> 动作代理视频
                          <span className="font-normal" style={{ color: "var(--color-text-muted)" }}>FFmpeg 去身份 · 临时动作参考</span>
                        </div>
                        <span
                          className="inline-flex h-6 items-center rounded-md px-2 text-[11px] font-semibold"
                          style={{
                            color: selectedMotionProxyPrepMeta.color,
                            backgroundColor: `color-mix(in srgb, ${selectedMotionProxyPrepMeta.color} 11%, transparent)`,
                            border: `1px solid color-mix(in srgb, ${selectedMotionProxyPrepMeta.color} 28%, transparent)`,
                          }}
                        >
                          代理{selectedMotionProxyPrepMeta.label}
                        </span>
                      </div>
                      <div className="grid grid-cols-[minmax(180px,0.8fr)_minmax(0,1.2fr)] gap-3 p-3">
                        <div className="aspect-video overflow-hidden rounded-md" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
                          {selectedMotionProxyUrl
                            ? <video src={selectedMotionProxyUrl} controls muted loop playsInline preload="metadata" className="h-full w-full bg-black object-contain" />
                            : <EmptyImage label={selectedMotionProxyPrepStatus === "failed" ? "动作代理生成失败" : "正在准备动作代理视频"} icon={Video} />}
                        </div>
                        <div className="min-w-0">
                          <p className="text-[11px] leading-5" style={{ color: "var(--color-text-secondary)" }}>
                            {selectedMotionProxyPrepMeta.description}
                          </p>
                          {selectedMotionProxyUrl && (
                            <div className="mt-2 rounded-md border px-2.5 py-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-secondary)" }}>
                              <div className="text-[10px] font-semibold" style={{ color: "var(--color-text-muted)" }}>临时 URL</div>
                              <p className="mt-1 truncate font-mono text-[10px]" title={selectedMotionProxyUrl} style={{ color: "var(--color-text-secondary)" }}>
                                {selectedMotionProxyUrl}
                              </p>
                            </div>
                          )}
                          {selectedMotionProxyDisplayError && (
                            <div className="mt-2 rounded-md border px-2 py-1.5 text-[10px] leading-4" role="alert" style={{ color: "var(--color-error)", borderColor: "color-mix(in srgb, var(--color-error) 30%, transparent)", backgroundColor: "color-mix(in srgb, var(--color-error) 7%, transparent)" }}>
                              {selectedMotionProxyDisplayError}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {shotDraft.generationStrategy === "keyframe" && (
                    <div className="overflow-hidden rounded-md border" style={{ borderColor: `color-mix(in srgb, ${selectedKeyframeStateMeta.color} 38%, var(--color-border-primary))`, backgroundColor: "var(--color-bg-overlay)" }}>
                    <div className="flex items-center justify-between gap-3 border-b px-3 py-2" style={{ borderColor: "var(--color-border-primary)" }}>
                      <div className="flex items-center gap-2 text-[11px] font-semibold" style={{ color: "var(--color-text-secondary)" }}>
                        <ImageIcon size={13} /> 本次提交关键帧
                        <span className="font-normal" style={{ color: "var(--color-text-muted)" }}>新任务使用 Dreamina v3 素材库；旧素材 ID 不复用</span>
                      </div>
                      <span
                        className="inline-flex h-6 items-center rounded-md px-2 text-[11px] font-semibold"
                        style={{
                          color: selectedKeyframeStateMeta.color,
                          backgroundColor: `color-mix(in srgb, ${selectedKeyframeStateMeta.color} 11%, transparent)`,
                          border: `1px solid color-mix(in srgb, ${selectedKeyframeStateMeta.color} 28%, transparent)`,
                        }}
                      >
                        {selectedKeyframeStateMeta.label}
                      </span>
                    </div>
                    <div className="grid grid-cols-[minmax(180px,0.8fr)_minmax(0,1.2fr)] gap-3 p-3">
                      <div className="aspect-video overflow-hidden rounded-md" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
                        {selectedShot.referenceFrameUrls?.[0]
                          ? <img src={selectedShot.referenceFrameUrls[0]} alt={`${selectedShot.shotKey} 本次提交关键帧`} loading="lazy" decoding="async" className="h-full w-full object-contain" />
                          : <EmptyImage label="当前镜头没有关键帧" icon={ImageIcon} />}
                      </div>
                      <div className="flex min-w-0 flex-col justify-between gap-2">
                        <div>
                          <p className="text-[11px] leading-5" style={{ color: "var(--color-text-secondary)" }}>{selectedKeyframeStateMeta.description}</p>
                          {selectedKeyframeAsset.assetId && (
                            <p className="mt-1 truncate font-mono text-[10px]" title={selectedKeyframeAsset.assetId} style={{ color: "var(--color-text-muted)" }}>
                              asset_id: {selectedKeyframeAsset.assetId}
                            </p>
                          )}
                          {selectedKeyframeDisplayError && (
                            <div className="mt-2 rounded-md border px-2 py-1.5 text-[10px] leading-4" role="alert" style={{ color: "var(--color-error)", borderColor: "color-mix(in srgb, var(--color-error) 30%, transparent)", backgroundColor: "color-mix(in srgb, var(--color-error) 7%, transparent)" }}>
                              {selectedKeyframeDisplayError}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={handlePrimaryKeyframeAction}
                            disabled={!selectedShot.referenceFrameUrls?.[0] || selectedKeyframeBusy || isAssetActive(selectedKeyframeAsset)}
                            aria-label={selectedKeyframePrimaryLabel}
                            aria-busy={selectedKeyframeBusy}
                            className="inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-[11px] font-semibold disabled:opacity-45"
                            style={{ color: "var(--color-text-on-accent)", backgroundColor: isAssetActive(selectedKeyframeAsset) ? "var(--color-success)" : "var(--color-accent-primary)" }}
                          >
                            {selectedKeyframeBusy
                              ? <Loader2 size={13} className="animate-spin" />
                              : isAssetActive(selectedKeyframeAsset)
                                ? <CheckCircle2 size={13} />
                                : <PackagePlus size={13} />}
                            {selectedKeyframePrimaryLabel}
                          </button>
                        </div>
                      </div>
                    </div>
                    </div>
                  )}

                  {selectedShot.errorMessage && <div className="rounded-md border p-2 text-[11px] leading-5" style={{ color: "var(--color-error)", borderColor: "color-mix(in srgb, var(--color-error) 30%, transparent)", backgroundColor: "color-mix(in srgb, var(--color-error) 7%, transparent)" }}>{selectedShot.errorMessage}</div>}
                  {selectedShotMissingMappingCount > 0 && <div className="rounded-md border p-2 text-[11px] leading-5" style={{ color: "var(--color-warning)", borderColor: "color-mix(in srgb, var(--color-warning) 32%, transparent)", backgroundColor: "color-mix(in srgb, var(--color-warning) 7%, transparent)" }}>当前镜头还有 {selectedShotMissingMappingCount} 项人物或场景没有绑定，确认后才能提交视频生成。</div>}
                  {selectedGenerationBlockReason && <div className="rounded-md border p-2 text-[11px] leading-5" role="alert" style={{ color: "var(--color-warning)", borderColor: "color-mix(in srgb, var(--color-warning) 32%, transparent)", backgroundColor: "color-mix(in srgb, var(--color-warning) 7%, transparent)" }}>{selectedGenerationBlockReason}，处理后才能提交当前镜头。</div>}

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="mb-1.5 block text-[11px] font-semibold" style={{ color: "var(--color-text-secondary)" }}>本镜头人物映射</label>
                      <div className="custom-scrollbar max-h-32 space-y-1 overflow-y-auto rounded-md border p-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-overlay)" }}>
                        {(selectedShot.sourceCharacterKeys || []).length === 0 && <div className="py-2 text-center text-[11px]" style={{ color: "var(--color-text-muted)" }}>本镜头没有识别到出镜人物</div>}
                        {(selectedShot.sourceCharacterKeys || []).map((sourceKey) => {
                          const source = sourceCharactersByKey.get(sourceKey);
                          const target = targetCharactersById.get(normalizeId(characterTargets[sourceKey]));
                          return (
                            <div key={sourceKey} className="flex items-center gap-2 rounded px-1.5 py-1.5 text-[11px]" style={{ backgroundColor: "var(--color-bg-secondary)" }}>
                              <span className="min-w-0 flex-1 truncate" style={{ color: "var(--color-text-secondary)" }}>{source?.sourceName || sourceKey}</span>
                              <ArrowRight size={11} className="shrink-0" style={{ color: "var(--color-text-muted)" }} />
                              <span className="min-w-0 flex-1 truncate font-semibold" style={{ color: target ? "var(--color-success)" : "var(--color-warning)" }}>{target?.name || "未绑定"}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <label className="mb-1.5 block text-[11px] font-semibold" style={{ color: "var(--color-text-secondary)" }}>本镜头场景映射</label>
                      <div className="flex h-9 items-center gap-2 rounded-md border px-2 text-[11px]" style={{ color: "var(--color-text-secondary)", backgroundColor: "var(--color-bg-overlay)", borderColor: "var(--color-border-primary)" }}>
                        <span className="min-w-0 flex-1 truncate">{sourceScenesByKey.get(selectedShot.sourceSceneKey)?.sourceName || selectedShot.sourceSceneKey || "无源场景"}</span>
                        <ArrowRight size={11} className="shrink-0" style={{ color: "var(--color-text-muted)" }} />
                        <span className="min-w-0 flex-1 truncate font-semibold" style={{ color: targetScenesById.get(normalizeId(sceneTargets[selectedShot.sourceSceneKey])) ? "var(--color-success)" : "var(--color-warning)" }}>{targetScenesById.get(normalizeId(sceneTargets[selectedShot.sourceSceneKey]))?.name || (selectedShot.sourceSceneKey ? "未绑定" : "无需绑定")}</span>
                      </div>
                      <p className="mt-1.5 text-[10px] leading-4" style={{ color: "var(--color-text-muted)" }}>人物和场景在左侧统一绑定，所有镜头按源人物顺序精确继承。</p>
                    </div>
                  </div>

                  <div>
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <div>
                        <div className="text-[11px] font-semibold" style={{ color: "var(--color-text-secondary)" }}>原片台词 → 英文逐句翻译</div>
                        <p className="mt-0.5 text-[10px] leading-4" style={{ color: "var(--color-text-muted)" }}>每句原文固定保留，只允许编辑对应英文；缺少任意一句翻译时禁止生成。</p>
                      </div>
                      <span className="shrink-0 text-[10px] font-mono-num" style={{ color: "var(--color-text-muted)" }}>
                        {(shotDraft.dialogueLines || []).length} 句
                      </span>
                    </div>
                    {(shotDraft.dialogueLines || []).length === 0 ? (
                      <div className="rounded-md border px-3 py-4 text-center text-[11px]" style={{ color: "var(--color-text-muted)", borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-overlay)" }}>
                        当前镜头没有识别到对白
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {(shotDraft.dialogueLines || []).map((line, index) => {
                          const inputId = `video-repaint-dialogue-${selectedShot.id}-${line.lineIndex}-${index}`;
                          const startTime = formatTimestamp(line.startSeconds);
                          const endTime = formatTimestamp(line.endSeconds);
                          const missingTranslation = Boolean(firstText(line.sourceText) && !firstText(line.englishText));
                          return (
                            <div
                              key={`${line.lineIndex}-${index}`}
                              className="overflow-hidden rounded-md border"
                              style={{
                                borderColor: missingTranslation
                                  ? "color-mix(in srgb, var(--color-warning) 42%, var(--color-border-primary))"
                                  : "var(--color-border-primary)",
                                backgroundColor: "var(--color-bg-overlay)",
                              }}
                            >
                              <div className="flex items-center justify-between gap-2 border-b px-2.5 py-1.5 text-[10px]" style={{ color: "var(--color-text-muted)", borderColor: "var(--color-border-primary)" }}>
                                <span>{firstText(line.speaker, `台词 ${index + 1}`)}</span>
                                <span className="font-mono-num">{startTime && endTime ? `${startTime} - ${endTime}` : `第 ${index + 1} 句`}</span>
                              </div>
                              <div className="grid grid-cols-2 gap-0">
                                <div className="border-r p-2.5" style={{ borderColor: "var(--color-border-primary)" }}>
                                  <div className="mb-1 text-[10px] font-semibold" style={{ color: "var(--color-text-muted)" }}>原片原文</div>
                                  <p className="whitespace-pre-wrap text-xs leading-5" style={{ color: "var(--color-text-primary)" }}>{line.sourceText || "未提供原文"}</p>
                                </div>
                                <div className="p-2.5">
                                  <label htmlFor={inputId} className="mb-1 block text-[10px] font-semibold" style={{ color: missingTranslation ? "var(--color-warning)" : "var(--color-text-muted)" }}>
                                    英文 1:1 翻译{missingTranslation ? "（必填）" : ""}
                                  </label>
                                  <textarea
                                    id={inputId}
                                    value={line.englishText || ""}
                                    onChange={(event) => {
                                      const englishText = event.target.value;
                                      setShotDraft((previous) => {
                                        const dialogueLines = (previous.dialogueLines || []).map((item, itemIndex) => (
                                          itemIndex === index ? { ...item, englishText } : item
                                        ));
                                        return {
                                          ...previous,
                                          dialogueLines,
                                          englishDialogue: dialogueLinesToEnglishDialogue(dialogueLines),
                                        };
                                      });
                                    }}
                                    className="min-h-16 w-full resize-y rounded-md p-2 text-xs leading-5 outline-none focus-visible:ring-2"
                                    style={{
                                      color: "var(--color-text-primary)",
                                      backgroundColor: "var(--color-bg-secondary)",
                                      border: `1px solid ${missingTranslation ? "var(--color-warning)" : "var(--color-border-primary)"}`,
                                      "--tw-ring-color": "var(--color-accent-primary)",
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div>
                    <label htmlFor="video-repaint-shot-prompt" className="mb-1.5 block text-[11px] font-semibold" style={{ color: "var(--color-text-secondary)" }}>
                      {shotDraft.generationStrategy === "keyframe" ? "视频转提示词（画面与动作描述）" : "动作代理复刻约束词"}
                    </label>
                    <textarea id="video-repaint-shot-prompt" value={shotDraft.prompt} onChange={(event) => setShotDraft((previous) => ({ ...previous, prompt: event.target.value }))} className="h-36 w-full resize-y rounded-md p-2.5 text-xs leading-5 outline-none" style={{ color: "var(--color-text-primary)", backgroundColor: "var(--color-bg-overlay)", border: "1px solid var(--color-border-primary)" }} />
                  </div>
                </div>
              )}
            </section>
          </main>
        </>
      )}
    </div>
  );
};

export default VideoRepaintWorkbench;
